import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../../layouts/AppLayout.jsx';
import ApiClient from '../../utils/ApiClient.js';
import CopyableEmail from '../../components/CopyableEmail';
import ValueWithSkeleton from '../../components/ValueWithSkeleton/ValueWithSkeleton';
import { firstResolvedDisplayName, normalizeDisplayValue } from '../../utils/DisplayValueUtils.js';
import { getFirstAttributeValue } from '../../utils/KeycloakAttributeUtils.js';
import { getUserTypeDisplayLabel } from '../../utils/UserTypeUtils.js';
import { formatRequestId } from '../../utils/RequestIdHelper.js';
import { formatStatusForDisplay } from '../../utils/StatusDisplayHelper.js';

const resolveRequesterOptionalField = (values) => {
    const candidates = Array.isArray(values) ? values : [values];
    for (const value of candidates) {
        const normalizedValue = normalizeDisplayValue(value);
        if (normalizedValue) {
            return normalizedValue;
        }
    }
    return '';
};
const resolveRequesterProfileField = (requester, attributeKeys, fallbackValues = []) => {
    const attributeValue = getFirstAttributeValue(requester?.attributes, attributeKeys, '');
    const fallbackList = Array.isArray(fallbackValues) ? fallbackValues : [fallbackValues];
    return resolveRequesterOptionalField([attributeValue, ...fallbackList]);
};

const ApprovalsPage = ({ authDetails, onSignOut }) => {
    const [supervisorReports, setSupervisorReports] = useState([]);
    const [supervisorReportsError, setSupervisorReportsError] = useState('');
    const [isLoadingSupervisorReports, setIsLoadingSupervisorReports] = useState(true);
    const [supervisorReportPhotoLookup, setSupervisorReportPhotoLookup] = useState(new Map());
    const [supervisorApprovalRequests, setSupervisorApprovalRequests] = useState([]);
    const [supervisorApprovalError, setSupervisorApprovalError] = useState('');
    const [isLoadingSupervisorApprovals, setIsLoadingSupervisorApprovals] = useState(true);
    const [isLoadingSupervisorGroupLookup, setIsLoadingSupervisorGroupLookup] = useState(false);
    const [isLoadingSupervisorRequesterLookup, setIsLoadingSupervisorRequesterLookup] = useState(false);
    const [selectedSupervisorApprovalIds, setSelectedSupervisorApprovalIds] = useState(new Set());
    const [expandedSupervisorApprovalIds, setExpandedSupervisorApprovalIds] = useState(new Set());
    const [supervisorApprovalComments, setSupervisorApprovalComments] = useState({});
    const [supervisorApprovalActionError, setSupervisorApprovalActionError] = useState('');
    const [supervisorApprovalSuccessMessage, setSupervisorApprovalSuccessMessage] = useState('');
    const [isSubmittingSupervisorAction, setIsSubmittingSupervisorAction] = useState(false);
    const [supervisorGroupLookup, setSupervisorGroupLookup] = useState(new Map());
    const [supervisorRequesterLookup, setSupervisorRequesterLookup] = useState(new Map());
    const [supervisorRequesterPhotoLookup, setSupervisorRequesterPhotoLookup] = useState(new Map());
    const [entitlementOwnerApprovalRequests, setEntitlementOwnerApprovalRequests] = useState([]);
    const [entitlementOwnerApprovalError, setEntitlementOwnerApprovalError] = useState('');
    const [isLoadingEntitlementOwnerApprovals, setIsLoadingEntitlementOwnerApprovals] = useState(true);
    const [selectedEntitlementOwnerApprovalIds, setSelectedEntitlementOwnerApprovalIds] = useState(new Set());
    const [expandedEntitlementOwnerApprovalIds, setExpandedEntitlementOwnerApprovalIds] = useState(new Set());
    const [entitlementOwnerApprovalComments, setEntitlementOwnerApprovalComments] = useState({});
    const [entitlementOwnerApprovalActionError, setEntitlementOwnerApprovalActionError] = useState('');
    const [entitlementOwnerApprovalSuccessMessage, setEntitlementOwnerApprovalSuccessMessage] = useState('');
    const [isSubmittingEntitlementOwnerAction, setIsSubmittingEntitlementOwnerAction] = useState(false);
    const [entitlementOwnerGroupLookup, setEntitlementOwnerGroupLookup] = useState(new Map());
    const [entitlementOwnerRequesterLookup, setEntitlementOwnerRequesterLookup] = useState(new Map());
    const [entitlementOwnerRequesterPhotoLookup, setEntitlementOwnerRequesterPhotoLookup] = useState(new Map());
    const [entitlementOwnerSupervisorLookup, setEntitlementOwnerSupervisorLookup] = useState(new Map());
    const [entitlementOwnerSupervisorPhotoLookup, setEntitlementOwnerSupervisorPhotoLookup] = useState(new Map());
    const [approverGroups, setApproverGroups] = useState([]);
    const [isLoadingApproverGroups, setIsLoadingApproverGroups] = useState(true);
    const [isLoadingEntitlementOwnerGroupLookup, setIsLoadingEntitlementOwnerGroupLookup] = useState(false);
    const [isLoadingEntitlementOwnerRequesterLookup, setIsLoadingEntitlementOwnerRequesterLookup] = useState(false);
    const [isLoadingEntitlementOwnerSupervisorLookup, setIsLoadingEntitlementOwnerSupervisorLookup] = useState(false);
    const [approverGroupPhotoLookup, setApproverGroupPhotoLookup] = useState(new Map());
    const [approverGroupPhotoUploadError, setApproverGroupPhotoUploadError] = useState('');
    const [isUploadingApproverGroupPhotoId, setIsUploadingApproverGroupPhotoId] = useState('');
    const [selectedApproverGroupPhotoId, setSelectedApproverGroupPhotoId] = useState('');
    const [approverGroupPhotoRefreshKey, setApproverGroupPhotoRefreshKey] = useState(0);
    const approverGroupPhotoInputRef = useRef(null);
    const tokenApproverGroupNames = useMemo(() => {
        const tokenGroups = authDetails?.identity?.tokenParsed?.groups ?? [];
        if (!Array.isArray(tokenGroups)) {
            return [];
        }
        return tokenGroups
            .map((group) => String(group ?? '').trim())
            .filter((groupName) => groupName.endsWith('-Approvers'));
    }, [authDetails]);
    const supervisorId =
        authDetails?.identity?.tokenParsed?.sub ??
        authDetails?.identity?.tokenParsed?.preferred_username ??
        authDetails?.identity?.tokenParsed?.email ??
        '';
    const currentUserEmail = authDetails?.identity?.tokenParsed?.email ?? '';
    const entitlementOwnerGroupUuids = useMemo(() => {
        return Array.from(
            new Set(
                approverGroups
                    .map((group) => String(group?.id ?? '').trim())
                    .filter(Boolean)
            )
        );
    }, [approverGroups]);
    const entitlementOwnerGroupUuidParam = useMemo(() => {
        return entitlementOwnerGroupUuids.join(',');
    }, [entitlementOwnerGroupUuids]);
    const toggleSupervisorApprovalSelection = (approvalId) => {
        setSelectedSupervisorApprovalIds((prev) => {
            const next = new Set(prev);
            if (next.has(approvalId)) {
                next.delete(approvalId);
            } else {
                next.add(approvalId);
            }
            return next;
        });
    };

    const toggleSupervisorApprovalDetails = (approvalId) => {
        setExpandedSupervisorApprovalIds((prev) => {
            const next = new Set(prev);
            if (next.has(approvalId)) {
                next.delete(approvalId);
            } else {
                next.add(approvalId);
            }
            return next;
        });
    };

    const toggleEntitlementOwnerApprovalSelection = (approvalId) => {
        setSelectedEntitlementOwnerApprovalIds((prev) => {
            const next = new Set(prev);
            if (next.has(approvalId)) {
                next.delete(approvalId);
            } else {
                next.add(approvalId);
            }
            return next;
        });
    };

    const toggleEntitlementOwnerApprovalDetails = (approvalId) => {
        setExpandedEntitlementOwnerApprovalIds((prev) => {
            const next = new Set(prev);
            if (next.has(approvalId)) {
                next.delete(approvalId);
            } else {
                next.add(approvalId);
            }
            return next;
        });
    };

    const isApprovalOverdue = (dateString) => {
        const parsedDate = new Date(dateString);
        if (Number.isNaN(parsedDate.getTime())) {
            return false;
        }
        const diffMs = Date.now() - parsedDate.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays > 15;
    };

    const normalizeSubmittedAt = useCallback((value) => {
        if (!value) {
            return null;
        }
        if (value instanceof Date) {
            return Number.isNaN(value.getTime()) ? null : value;
        }
        const rawValue = String(value).trim();
        if (!rawValue) {
            return null;
        }
        const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(rawValue);
        const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
        const parsed = new Date(normalizedValue);
        if (!Number.isNaN(parsed.getTime())) {
            return parsed;
        }
        const fallback = new Date(rawValue);
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    }, []);

    const normalizeGroupId = (group) => {
        if (!group) {
            return null;
        }
        if (typeof group === 'string' || typeof group === 'number') {
            return String(group);
        }
        if (group.id || group.uuid) {
            return String(group.id ?? group.uuid);
        }
        return null;
    };
    const getRequestedEntitlementSummary = (requestedGroupName, requestedRoleNames) => {
        const roleNames = Array.isArray(requestedRoleNames)
            ? requestedRoleNames.filter(Boolean)
            : [];

        if (roleNames.length > 0) {
            return {
                requestedEntitlementLabel: 'Requested roles',
                requestedEntitlementDisplay: roleNames.join(', '),
            };
        }

        return {
            requestedEntitlementLabel: 'Requested groups',
            requestedEntitlementDisplay: requestedGroupName || 'None listed',
        };
    };

    const extractRequestList = (responseData) => {
        if (Array.isArray(responseData)) {
            return responseData;
        }
        return responseData?.content ?? responseData?.requests ?? [];
    };

    const resolveApprovalRequestIdentifier = useCallback((request, fallbackNumber) => {
        const candidates = [
            request?.requestNumber,
            request?.requestId,
            request?.accessRequestId,
            request?.accessRequestNumber,
            request?.id,
            fallbackNumber,
        ];
        const rawCandidate = candidates.find((candidate) => {
            if (candidate === null || candidate === undefined) {
                return false;
            }
            return Boolean(String(candidate).trim());
        });
        const normalizedCandidate = String(rawCandidate ?? fallbackNumber).trim();
        const reqLikeMatch = normalizedCandidate.match(/^req[-_\s]?(\d+)$/i);
        return String(reqLikeMatch ? reqLikeMatch[1] : normalizedCandidate).trim();
    }, []);

    const formatApprovalRequestId = useCallback((request, fallbackNumber) => {
        const requestIdValue = resolveApprovalRequestIdentifier(request, fallbackNumber);
        return formatRequestId(requestIdValue);
    }, [resolveApprovalRequestIdentifier]);

    const resolveApprovalSubmittedAt = useCallback((request) => {
        const submittedAtRaw =
            request?.createdAt ??
            request?.submittedAt ??
            request?.submittedDate ??
            request?.createdDate ??
            request?.requestedAt ??
            request?.createdOn ??
            request?.created ??
            request?.requestDate ??
            request?.dateSubmitted ??
            request?.dateCreated ??
            request?.submissionDate ??
            request?.submissionTimestamp ??
            request?.timestamp ??
            '';

        return normalizeSubmittedAt(submittedAtRaw);
    }, [normalizeSubmittedAt]);

    const isSubmittedAtOverdue = useCallback((submittedAt) => {
        if (!submittedAt) {
            return false;
        }
        const diffMs = Date.now() - submittedAt.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays > 15;
    }, []);

    const compareApprovalRequestsForDisplay = useCallback((left, right) => {
        const leftSubmittedAt = resolveApprovalSubmittedAt(left);
        const rightSubmittedAt = resolveApprovalSubmittedAt(right);
        const leftIsOverdue = isSubmittedAtOverdue(leftSubmittedAt);
        const rightIsOverdue = isSubmittedAtOverdue(rightSubmittedAt);

        if (leftIsOverdue !== rightIsOverdue) {
            return leftIsOverdue ? -1 : 1;
        }

        const leftTime = leftSubmittedAt ? leftSubmittedAt.getTime() : Number.POSITIVE_INFINITY;
        const rightTime = rightSubmittedAt ? rightSubmittedAt.getTime() : Number.POSITIVE_INFINITY;
        if (leftTime !== rightTime) {
            return leftTime - rightTime;
        }

        const leftIdentifier = resolveApprovalRequestIdentifier(left, Number.MAX_SAFE_INTEGER);
        const rightIdentifier = resolveApprovalRequestIdentifier(right, Number.MAX_SAFE_INTEGER);
        const identifierDelta = leftIdentifier.localeCompare(rightIdentifier, undefined, {
            sensitivity: 'base',
            numeric: true,
        });
        if (identifierDelta !== 0) {
            return identifierDelta;
        }

        const leftId = String(left?.id ?? '').trim();
        const rightId = String(right?.id ?? '').trim();
        return leftId.localeCompare(rightId, undefined, {
            sensitivity: 'base',
            numeric: true,
        });
    }, [isSubmittedAtOverdue, resolveApprovalRequestIdentifier, resolveApprovalSubmittedAt]);

    const extractReviewList = useCallback((responseData) => {
        if (Array.isArray(responseData)) {
            return responseData;
        }
        return responseData?.content ?? responseData?.reviews ?? [];
    }, []);

    const isFinalReviewDecision = useCallback((decision) => {
        const normalizedDecision = String(decision ?? '').trim().toUpperCase();
        return Boolean(normalizedDecision) && normalizedDecision !== 'DRAFT';
    }, []);

    const getLatestReviewByType = useCallback((reviews, reviewType) => {
        const normalizedType = String(reviewType ?? '').trim().toUpperCase();
        const reviewList = Array.isArray(reviews) ? reviews : [];

        const candidates = reviewList.filter((review) => {
            const candidateType = String(review?.type ?? '').trim().toUpperCase();
            return candidateType === normalizedType && isFinalReviewDecision(review?.decision);
        });

        if (candidates.length === 0) {
            return null;
        }

        const resolveCreatedAt = (review) => {
            const parsed = normalizeSubmittedAt(review?.createdAt ?? review?.updatedAt ?? null);
            return parsed ? parsed.getTime() : Number.NEGATIVE_INFINITY;
        };

        return candidates.sort((left, right) => {
            const createdAtDelta = resolveCreatedAt(right) - resolveCreatedAt(left);
            if (createdAtDelta !== 0) {
                return createdAtDelta;
            }

            const leftId = Number(left?.id ?? 0);
            const rightId = Number(right?.id ?? 0);
            return rightId - leftId;
        })[0];
    }, [isFinalReviewDecision, normalizeSubmittedAt]);

    const enrichRequestsWithSupervisorReview = useCallback(async (requests) => {
        const requestList = Array.isArray(requests) ? requests : [];

        return Promise.all(
            requestList.map(async (request) => {
                if (request?.supervisorReview?.decision) {
                    return request;
                }

                const requestId = String(request?.id ?? '').trim();
                if (!requestId) {
                    return request;
                }

                try {
                    const reviewResponse = await ApiClient.get(
                        `/access-requests/${encodeURIComponent(requestId)}/review`
                    );

                    if (reviewResponse.status !== 200) {
                        return request;
                    }

                    const reviewList = extractReviewList(reviewResponse.data ?? {});
                    const supervisorReview = getLatestReviewByType(reviewList, 'SUPERVISOR');

                    if (!supervisorReview) {
                        return request;
                    }

                    return {
                        ...request,
                        supervisorReview,
                    };
                } catch (error) {
                    return request;
                }
            })
        );
    }, [extractReviewList, getLatestReviewByType]);

    const updateSupervisorApprovalComment = (approvalId, value) => {
        setSupervisorApprovalComments((prev) => ({
            ...prev,
            [approvalId]: value,
        }));
    };

    const updateEntitlementOwnerApprovalComment = (approvalId, value) => {
        setEntitlementOwnerApprovalComments((prev) => ({
            ...prev,
            [approvalId]: value,
        }));
    };

    const handleApproverGroupPhotoUploadClick = (groupId) => {
        const normalizedGroupId = String(groupId ?? '').trim();
        if (!normalizedGroupId) {
            setApproverGroupPhotoUploadError('Group identifier is missing. Please refresh and try again.');
            return;
        }

        setApproverGroupPhotoUploadError('');
        setSelectedApproverGroupPhotoId(normalizedGroupId);
        approverGroupPhotoInputRef.current?.click();
    };

    const handleApproverGroupPhotoChange = async (event) => {
        const file = event.target.files?.[0];
        const groupId = String(selectedApproverGroupPhotoId ?? '').trim();

        if (!file || !file.type.startsWith('image/')) {
            event.target.value = '';
            return;
        }

        if (!groupId) {
            setApproverGroupPhotoUploadError('Group identifier is missing. Please refresh and try again.');
            event.target.value = '';
            return;
        }

        setApproverGroupPhotoUploadError('');
        setIsUploadingApproverGroupPhotoId(groupId);

        const formData = new FormData();
        formData.append('image', file);

        try {
            await ApiClient.post(`/groups/${encodeURIComponent(groupId)}/picture`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setApproverGroupPhotoRefreshKey((prev) => prev + 1);
        } catch (error) {
            setApproverGroupPhotoUploadError('We could not upload the group photo. Please try again.');
        } finally {
            setIsUploadingApproverGroupPhotoId('');
            setSelectedApproverGroupPhotoId('');
            event.target.value = '';
        }
    };

    const resolveNotes = (commentsLookup, approvalId) => {
        return (commentsLookup?.[approvalId] ?? '').trim();
    };

    const submitReviewRequests = async ({
        approvalIds,
        decision,
        notesLookup,
        setError,
        setSubmitting,
        setSuccessMessage = () => {},
        onSuccess,
        reviewType,
    }) => {
        const ids = Array.from(approvalIds);

        if (ids.length === 0) {
            return;
        }

        const missingNotes =
            decision === 'DENIED'
                ? ids.filter((id) => !resolveNotes(notesLookup, id))
                : [];

        if (missingNotes.length > 0) {
            setError('Approver comments are required when rejecting a request.');
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccessMessage('');

        try {
            await Promise.all(
                ids.map((id) =>
                    ApiClient.post(`/access-requests/${encodeURIComponent(id)}/review`, {
                        type: reviewType,
                        decision,
                        notes: resolveNotes(notesLookup, id),
                    })
                )
            );

            onSuccess(ids);
        } catch (error) {
            setError('Unable to submit approval decisions. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    useEffect(() => {
        const bannerMessage = window.sessionStorage.getItem('supervisorApprovalSuccessMessage');
        if (bannerMessage) {
            setSupervisorApprovalSuccessMessage(bannerMessage);
            window.sessionStorage.removeItem('supervisorApprovalSuccessMessage');
        }
    }, []);

    useEffect(() => {
        const bannerMessage = window.sessionStorage.getItem('entitlementOwnerApprovalSuccessMessage');
        if (bannerMessage) {
            setEntitlementOwnerApprovalSuccessMessage(bannerMessage);
            window.sessionStorage.removeItem('entitlementOwnerApprovalSuccessMessage');
        }
    }, []);

    useEffect(() => {
        let isMounted = true;
        const subject = String(
            authDetails?.identity?.tokenParsed?.sub ?? authDetails?.identity?.subject ?? ''
        ).trim();
        const fallbackGroups = tokenApproverGroupNames.map((name) => ({
            id: '',
            name,
        }));

        if (!subject) {
            setApproverGroups(fallbackGroups);
            setIsLoadingApproverGroups(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchApproverGroups = async () => {
            setIsLoadingApproverGroups(true);
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(subject)}/groups`);

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const responseData = response.data ?? {};
                const groups = Array.isArray(responseData)
                    ? responseData
                    : responseData.content ?? responseData.groups ?? [];
                const filteredGroups = (Array.isArray(groups) ? groups : [])
                    .map((group) => {
                        const name = String(
                            group?.name ?? group?.displayName ?? group?.id ?? group?.uuid ?? ''
                        ).trim();
                        return {
                            id: String(normalizeGroupId(group) ?? '').trim(),
                            name,
                        };
                    })
                    .filter((group) => group.name.endsWith('-Approvers'));

                if (isMounted) {
                    setApproverGroups(filteredGroups.length > 0 ? filteredGroups : fallbackGroups);
                }
            } catch (error) {
                if (isMounted) {
                    setApproverGroups(fallbackGroups);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingApproverGroups(false);
                }
            }
        };

        fetchApproverGroups();

        return () => {
            isMounted = false;
        };
    }, [authDetails, tokenApproverGroupNames]);

    useEffect(() => {
        let isMounted = true;
        let objectUrls = [];
        const groupIds = new Set();

        approverGroups.forEach((group) => {
            const groupId = String(group?.id ?? '').trim();
            if (groupId) {
                groupIds.add(groupId);
            }
        });

        if (groupIds.size === 0) {
            setApproverGroupPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchApproverGroupPhotos = async () => {
            try {
                const responses = await Promise.all(
                    Array.from(groupIds).map(async (groupId) => {
                        try {
                            const response = await ApiClient.get(
                                `/groups/${encodeURIComponent(groupId)}/picture`,
                                {
                                    responseType: 'blob',
                                }
                            );

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                groupId,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    if (result?.groupId && result?.objectUrl) {
                        lookup.set(result.groupId, result.objectUrl);
                    }
                });

                if (isMounted) {
                    setApproverGroupPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setApproverGroupPhotoLookup(new Map());
                }
            }
        };

        fetchApproverGroupPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                URL.revokeObjectURL(objectUrl);
            });
            objectUrls = [];
        };
    }, [approverGroups, approverGroupPhotoRefreshKey]);

    useEffect(() => {
        let isMounted = true;
        const supervisorUuid = String(supervisorId || '').trim();

        if (!supervisorUuid) {
            setSupervisorReports([]);
            setSupervisorReportsError('Supervisor identity is not available.');
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorReports = async () => {
            setIsLoadingSupervisorReports(true);
            setSupervisorReportsError('');
            try {
                const response = await ApiClient.get(`/users/${encodeURIComponent(supervisorUuid)}/directreports`);
                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }
                const reports = Array.isArray(response.data)
                    ? response.data
                    : response.data?.content ?? response.data?.users ?? response.data?.directReports ?? [];
                if (isMounted) {
                    setSupervisorReports(Array.isArray(reports) ? reports : []);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorReports([]);
                    setSupervisorReportsError('Unable to load supervisor reports.');
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisorReports(false);
                }
            }
        };

        fetchSupervisorReports();

        return () => {
            isMounted = false;
        };
    }, [supervisorId]);

    useEffect(() => {
        let isMounted = true;
        let objectUrls = [];
        const reportIds = new Set();

        supervisorReports.forEach((report) => {
            const reportId = report?.uuid ?? report?.id ?? report?.userId ?? report?.user_id ?? null;
            if (reportId) {
                reportIds.add(String(reportId));
            }
        });

        if (reportIds.size === 0) {
            setSupervisorReportPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchReportPhotos = async () => {
            try {
                const responses = await Promise.all(
                    Array.from(reportIds).map(async (reportId) => {
                        try {
                            const response = await ApiClient.get(
                                `/users/${encodeURIComponent(reportId)}/picture`,
                                {
                                    responseType: 'blob',
                                }
                            );

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                reportId,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    if (result?.reportId && result?.objectUrl) {
                        lookup.set(result.reportId, result.objectUrl);
                    }
                });

                if (isMounted) {
                    setSupervisorReportPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorReportPhotoLookup(new Map());
                }
            }
        };

        fetchReportPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                URL.revokeObjectURL(objectUrl);
            });
            objectUrls = [];
        };
    }, [supervisorReports]);

    useEffect(() => {
        let isMounted = true;
        const groupIds = new Set();

        supervisorApprovalRequests.forEach((request) => {
            const groupId = normalizeGroupId(request?.groupUuid);
            if (groupId) {
                groupIds.add(groupId);
            }
        });

        if (groupIds.size === 0) {
            setSupervisorGroupLookup(new Map());
            setIsLoadingSupervisorGroupLookup(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchGroupNames = async () => {
            setIsLoadingSupervisorGroupLookup(true);
            try {
                const responses = await Promise.all(
                    Array.from(groupIds).map((groupId) =>
                        ApiClient.get(`/groups/${encodeURIComponent(groupId)}`)
                            .then((response) => ({
                                groupId,
                                data: response.data,
                            }))
                            .catch(() => null)
                    )
                );
                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    const name = firstResolvedDisplayName(
                        [result?.data?.name, result?.data?.displayName],
                        'Unknown group'
                    );
                    if (result?.groupId && name) {
                        lookup.set(result.groupId, name);
                    }
                });

                if (isMounted) {
                    setSupervisorGroupLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorGroupLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisorGroupLookup(false);
                }
            }
        };

        fetchGroupNames();

        return () => {
            isMounted = false;
        };
    }, [supervisorApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const groupIds = new Set();

        entitlementOwnerApprovalRequests.forEach((request) => {
            const groupId = normalizeGroupId(request?.groupUuid);
            if (groupId) {
                groupIds.add(groupId);
            }
        });

        if (groupIds.size === 0) {
            setEntitlementOwnerGroupLookup(new Map());
            setIsLoadingEntitlementOwnerGroupLookup(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchGroupNames = async () => {
            setIsLoadingEntitlementOwnerGroupLookup(true);
            try {
                const responses = await Promise.all(
                    Array.from(groupIds).map((groupId) =>
                        ApiClient.get(`/groups/${encodeURIComponent(groupId)}`)
                            .then((response) => ({
                                groupId,
                                data: response.data,
                            }))
                            .catch(() => null)
                    )
                );
                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    const name = firstResolvedDisplayName(
                        [result?.data?.name, result?.data?.displayName],
                        'Unknown group'
                    );
                    if (result?.groupId && name) {
                        lookup.set(result.groupId, name);
                    }
                });

                if (isMounted) {
                    setEntitlementOwnerGroupLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerGroupLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementOwnerGroupLookup(false);
                }
            }
        };

        fetchGroupNames();

        return () => {
            isMounted = false;
        };
    }, [entitlementOwnerApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const requesterIds = new Set();

        supervisorApprovalRequests.forEach((request) => {
            const requesterId = request?.requesterUuid ?? null;
            if (requesterId) {
                requesterIds.add(String(requesterId));
            }
        });

        if (requesterIds.size === 0) {
            setSupervisorRequesterLookup(new Map());
            setIsLoadingSupervisorRequesterLookup(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequesterDetails = async () => {
            setIsLoadingSupervisorRequesterLookup(true);
            try {
                const responses = await Promise.all(
                    Array.from(requesterIds).map((requesterId) =>
                        ApiClient.get(`/users/${encodeURIComponent(requesterId)}`)
                            .then((response) => ({
                                requesterId,
                                data: response.data,
                            }))
                            .catch(() => null)
                    )
                );
                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    const requester = result?.data ?? {};
                    if (result?.requesterId) {
                        lookup.set(result.requesterId, requester);
                    }
                });

                if (isMounted) {
                    setSupervisorRequesterLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorRequesterLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisorRequesterLookup(false);
                }
            }
        };

        fetchRequesterDetails();

        return () => {
            isMounted = false;
        };
    }, [supervisorApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const requesterIds = new Set();

        entitlementOwnerApprovalRequests.forEach((request) => {
            const requesterId = request?.requesterUuid ?? null;
            if (requesterId) {
                requesterIds.add(String(requesterId));
            }
        });

        if (requesterIds.size === 0) {
            setEntitlementOwnerRequesterLookup(new Map());
            setIsLoadingEntitlementOwnerRequesterLookup(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchRequesterDetails = async () => {
            setIsLoadingEntitlementOwnerRequesterLookup(true);
            try {
                const responses = await Promise.all(
                    Array.from(requesterIds).map((requesterId) =>
                        ApiClient.get(`/users/${encodeURIComponent(requesterId)}`)
                            .then((response) => ({
                                requesterId,
                                data: response.data,
                            }))
                            .catch(() => null)
                    )
                );
                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    const requester = result?.data ?? {};
                    if (result?.requesterId) {
                        lookup.set(result.requesterId, requester);
                    }
                });

                if (isMounted) {
                    setEntitlementOwnerRequesterLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerRequesterLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementOwnerRequesterLookup(false);
                }
            }
        };

        fetchRequesterDetails();

        return () => {
            isMounted = false;
        };
    }, [entitlementOwnerApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const requesterIds = new Set();
        let objectUrls = [];

        supervisorApprovalRequests.forEach((request) => {
            const requesterId = request?.requesterUuid ?? null;
            if (requesterId) {
                requesterIds.add(String(requesterId));
            }
        });

        if (requesterIds.size === 0) {
            setSupervisorRequesterPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchRequesterPhotos = async () => {
            try {
                const responses = await Promise.all(
                    Array.from(requesterIds).map(async (requesterId) => {
                        try {
                            const response = await ApiClient.get(
                                `/users/${encodeURIComponent(requesterId)}/picture`,
                                {
                                    responseType: 'blob',
                                }
                            );

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                requesterId,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    if (result?.requesterId && result?.objectUrl) {
                        lookup.set(result.requesterId, result.objectUrl);
                    }
                });

                if (isMounted) {
                    setSupervisorRequesterPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorRequesterPhotoLookup(new Map());
                }
            }
        };

        fetchRequesterPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                URL.revokeObjectURL(objectUrl);
            });
            objectUrls = [];
        };
    }, [supervisorApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const requesterIds = new Set();
        let objectUrls = [];

        entitlementOwnerApprovalRequests.forEach((request) => {
            const requesterId = request?.requesterUuid ?? null;
            if (requesterId) {
                requesterIds.add(String(requesterId));
            }
        });

        if (requesterIds.size === 0) {
            setEntitlementOwnerRequesterPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchRequesterPhotos = async () => {
            try {
                const responses = await Promise.all(
                    Array.from(requesterIds).map(async (requesterId) => {
                        try {
                            const response = await ApiClient.get(
                                `/users/${encodeURIComponent(requesterId)}/picture`,
                                {
                                    responseType: 'blob',
                                }
                            );

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                requesterId,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    if (result?.requesterId && result?.objectUrl) {
                        lookup.set(result.requesterId, result.objectUrl);
                    }
                });

                if (isMounted) {
                    setEntitlementOwnerRequesterPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerRequesterPhotoLookup(new Map());
                }
            }
        };

        fetchRequesterPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                URL.revokeObjectURL(objectUrl);
            });
            objectUrls = [];
        };
    }, [entitlementOwnerApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const supervisorUuid = String(supervisorId || '').trim();

        if (!supervisorUuid) {
            setSupervisorApprovalRequests([]);
            setSupervisorApprovalError('Supervisor identity is not available.');
            setIsLoadingSupervisorGroupLookup(false);
            setIsLoadingSupervisorRequesterLookup(false);
            setIsLoadingSupervisorApprovals(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorApprovalTasks = async () => {
            setIsLoadingSupervisorApprovals(true);
            setSupervisorApprovalError('');
            try {
                const response = await ApiClient.get('/access-requests', {
                    params: {
                        page: 0,
                        size: 100,
                        supervisor: supervisorUuid,
                        state: 'SUBMITTED',
                    },
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const responseData = response.data ?? {};
                const requestList = Array.isArray(responseData)
                    ? responseData
                    : responseData.content ?? responseData.requests ?? [];
                const filteredRequests = (Array.isArray(requestList) ? requestList : []).filter((request) => {
                    const status = String(request?.state ?? request?.status ?? '').toUpperCase();
                    const requestSupervisor =
                        request?.supervisorUuid ??
                        request?.supervisorId ??
                        request?.supervisorUserId ??
                        request?.supervisorUserUuid ??
                        request?.supervisor ??
                        '';
                    return status === 'SUBMITTED' && String(requestSupervisor).trim() === supervisorUuid;
                });

                if (isMounted) {
                    setIsLoadingSupervisorGroupLookup(filteredRequests.length > 0);
                    setIsLoadingSupervisorRequesterLookup(filteredRequests.length > 0);
                    setSupervisorApprovalRequests(filteredRequests);
                }
            } catch (error) {
                if (isMounted) {
                    setSupervisorApprovalRequests([]);
                    setSupervisorApprovalError('Unable to load supervisor approval tasks.');
                    setIsLoadingSupervisorGroupLookup(false);
                    setIsLoadingSupervisorRequesterLookup(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingSupervisorApprovals(false);
                }
            }
        };

        fetchSupervisorApprovalTasks();

        return () => {
            isMounted = false;
        };
    }, [supervisorId]);

    useEffect(() => {
        let isMounted = true;

        if (isLoadingApproverGroups) {
            setEntitlementOwnerApprovalError('');
            setEntitlementOwnerApprovalRequests([]);
            setIsLoadingEntitlementOwnerApprovals(true);
            return () => {
                isMounted = false;
            };
        }

        if (!entitlementOwnerGroupUuidParam) {
            setEntitlementOwnerApprovalRequests([]);
            setEntitlementOwnerApprovalError('Entitlement owner approval groups are not available.');
            setIsLoadingEntitlementOwnerGroupLookup(false);
            setIsLoadingEntitlementOwnerRequesterLookup(false);
            setIsLoadingEntitlementOwnerSupervisorLookup(false);
            setIsLoadingEntitlementOwnerApprovals(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchEntitlementOwnerApprovalTasks = async () => {
            setIsLoadingEntitlementOwnerApprovals(true);
            setEntitlementOwnerApprovalError('');
            try {
                const response = await ApiClient.get('/access-requests', {
                    params: {
                        page: 0,
                        size: 100,
                        entitlementOwner: entitlementOwnerGroupUuidParam,
                    },
                });

                if (response.status !== 200) {
                    throw new Error(`API responded with ${response.status}`);
                }

                const responseData = response.data ?? {};
                const requestList = extractRequestList(responseData);
                const requestsWithReviewState = await enrichRequestsWithSupervisorReview(
                    Array.isArray(requestList) ? requestList : []
                );
                const filteredRequests = requestsWithReviewState.filter((request) => {
                    const status = String(request?.state ?? request?.status ?? '').toUpperCase();
                    const supervisorDecision = String(request?.supervisorReview?.decision ?? '').toUpperCase();
                    return status === 'IN_PROGRESS' && supervisorDecision === 'APPROVED';
                });

                if (isMounted) {
                    setIsLoadingEntitlementOwnerGroupLookup(filteredRequests.length > 0);
                    setIsLoadingEntitlementOwnerRequesterLookup(filteredRequests.length > 0);
                    setIsLoadingEntitlementOwnerSupervisorLookup(filteredRequests.length > 0);
                    setEntitlementOwnerApprovalRequests(filteredRequests);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerApprovalRequests([]);
                    setEntitlementOwnerApprovalError('Unable to load entitlement owner approval tasks.');
                    setIsLoadingEntitlementOwnerGroupLookup(false);
                    setIsLoadingEntitlementOwnerRequesterLookup(false);
                    setIsLoadingEntitlementOwnerSupervisorLookup(false);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementOwnerApprovals(false);
                }
            }
        };

        fetchEntitlementOwnerApprovalTasks();

        return () => {
            isMounted = false;
        };
    }, [entitlementOwnerGroupUuidParam, enrichRequestsWithSupervisorReview, isLoadingApproverGroups]);

    useEffect(() => {
        let isMounted = true;
        const supervisorIds = new Set();

        entitlementOwnerApprovalRequests.forEach((request) => {
            const supervisorId = request?.supervisorUuid ?? null;
            if (supervisorId) {
                supervisorIds.add(String(supervisorId));
            }
        });

        if (supervisorIds.size === 0) {
            setEntitlementOwnerSupervisorLookup(new Map());
            setIsLoadingEntitlementOwnerSupervisorLookup(false);
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorDetails = async () => {
            setIsLoadingEntitlementOwnerSupervisorLookup(true);
            try {
                const responses = await Promise.all(
                    Array.from(supervisorIds).map((supervisorId) =>
                        ApiClient.get(`/users/${encodeURIComponent(supervisorId)}`)
                            .then((response) => ({
                                supervisorId,
                                data: response.data,
                            }))
                            .catch(() => null)
                    )
                );
                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    const supervisor = result?.data ?? {};
                    if (result?.supervisorId) {
                        lookup.set(result.supervisorId, supervisor);
                    }
                });

                if (isMounted) {
                    setEntitlementOwnerSupervisorLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerSupervisorLookup(new Map());
                }
            } finally {
                if (isMounted) {
                    setIsLoadingEntitlementOwnerSupervisorLookup(false);
                }
            }
        };

        fetchSupervisorDetails();

        return () => {
            isMounted = false;
        };
    }, [entitlementOwnerApprovalRequests]);

    useEffect(() => {
        let isMounted = true;
        const supervisorIds = new Set();
        let objectUrls = [];

        entitlementOwnerApprovalRequests.forEach((request) => {
            const supervisorId = request?.supervisorUuid ?? null;
            if (supervisorId) {
                supervisorIds.add(String(supervisorId));
            }
        });

        if (supervisorIds.size === 0) {
            setEntitlementOwnerSupervisorPhotoLookup(new Map());
            return () => {
                isMounted = false;
            };
        }

        const fetchSupervisorPhotos = async () => {
            try {
                const responses = await Promise.all(
                    Array.from(supervisorIds).map(async (supervisorId) => {
                        try {
                            const response = await ApiClient.get(
                                `/users/${encodeURIComponent(supervisorId)}/picture`,
                                {
                                    responseType: 'blob',
                                }
                            );

                            if (response.status !== 200) {
                                throw new Error(`API responded with ${response.status}`);
                            }

                            const contentType = response.headers?.['content-type'] ?? '';
                            if (contentType && !contentType.startsWith('image/')) {
                                throw new Error(`Unexpected content type: ${contentType}`);
                            }

                            const objectUrl = URL.createObjectURL(response.data);
                            objectUrls = [...objectUrls, objectUrl];

                            return {
                                supervisorId,
                                objectUrl,
                            };
                        } catch (error) {
                            return null;
                        }
                    })
                );

                const lookup = new Map();
                responses.filter(Boolean).forEach((result) => {
                    if (result?.supervisorId && result?.objectUrl) {
                        lookup.set(result.supervisorId, result.objectUrl);
                    }
                });

                if (isMounted) {
                    setEntitlementOwnerSupervisorPhotoLookup(lookup);
                }
            } catch (error) {
                if (isMounted) {
                    setEntitlementOwnerSupervisorPhotoLookup(new Map());
                }
            }
        };

        fetchSupervisorPhotos();

        return () => {
            isMounted = false;
            objectUrls.forEach((objectUrl) => {
                URL.revokeObjectURL(objectUrl);
            });
            objectUrls = [];
        };
    }, [entitlementOwnerApprovalRequests]);


    const sortedSupervisorApprovalRequests = useMemo(() => {
        return [...supervisorApprovalRequests].sort(compareApprovalRequestsForDisplay);
    }, [compareApprovalRequestsForDisplay, supervisorApprovalRequests]);

    const supervisorApprovalTasks = useMemo(() => {
        return sortedSupervisorApprovalRequests.map((request, index) => {
            const requesterId = request?.requesterUuid ?? null;
            const resolvedRequester = requesterId
                ? supervisorRequesterLookup.get(String(requesterId))
                : null;
            const requesterPhotoUrl = requesterId
                ? supervisorRequesterPhotoLookup.get(String(requesterId))
                : null;
            const resolvedRequesterFirstName = resolvedRequester?.firstName ?? '';
            const resolvedRequesterLastName = resolvedRequester?.lastName ?? '';
            const resolvedRequesterUsername =
                resolvedRequester?.preferred_username ?? resolvedRequester?.username ?? '';
            const resolvedRequesterEmail = resolvedRequester?.email ?? '';
            const resolvedRequesterDisplayName =
                resolvedRequester?.name ??
                resolvedRequester?.displayName ??
                `${resolvedRequesterFirstName} ${resolvedRequesterLastName}`.trim() ??
                resolvedRequesterUsername ??
                resolvedRequesterEmail ??
                '';
            const requesterDisplayName =
                resolvedRequesterDisplayName ||
                'Unknown user';
            const resolvedRequesterFirstNameDisplay =
                resolvedRequesterFirstName || 'Not provided';
            const resolvedRequesterLastNameDisplay =
                resolvedRequesterLastName || 'Not provided';
            const resolvedRequesterUsernameDisplay =
                resolvedRequesterUsername || 'Not provided';
            const resolvedRequesterEmailDisplay =
                resolvedRequesterEmail || 'Not provided';
            const resolvedRequesterJobTitle = resolveRequesterProfileField(
                resolvedRequester,
                ['jobTitle', 'JobTitle'],
                [
                    resolvedRequester?.jobTitle,
                    resolvedRequester?.job_title,
                    request?.requesterJobTitle,
                    request?.jobTitle,
                ]
            );
            const resolvedRequesterUserType = getUserTypeDisplayLabel(
                resolveRequesterProfileField(
                    resolvedRequester,
                    ['UserType', 'userType', 'user_type'],
                    [
                        resolvedRequester?.userType,
                        resolvedRequester?.UserType,
                        resolvedRequester?.user_type,
                        request?.requesterUserType,
                        request?.requesterUsertype,
                        request?.userType,
                        request?.UserType,
                    ]
                ),
                ''
            );
            const resolvedRequesterContractNumber = resolveRequesterProfileField(
                resolvedRequester,
                ['contractNumber', 'ContractNumber'],
                [
                    resolvedRequester?.contractNumber,
                    resolvedRequester?.contract_number,
                    request?.requesterContractNumber,
                    request?.contractNumber,
                ]
            );
            const resolvedRequesterContractExpirationDate = resolveRequesterProfileField(
                resolvedRequester,
                ['contractExpirationDate', 'ContractExpirationDate'],
                [
                    resolvedRequester?.contractExpirationDate,
                    resolvedRequester?.contract_expiration_date,
                    request?.requesterContractExpirationDate,
                    request?.contractExpirationDate,
                ]
            );
            const requestId =
                request?.id ?? `SUP-${index + 1}`;
            const requestDisplayId = formatApprovalRequestId(request, index + 1);
            const submittedAtRaw =
                request?.createdAt ??
                request?.submittedAt ??
                request?.submittedDate ??
                request?.createdDate ??
                request?.requestedAt ??
                request?.createdOn ??
                request?.created ??
                request?.requestDate ??
                request?.dateSubmitted ??
                request?.dateCreated ??
                request?.submissionDate ??
                request?.submissionTimestamp ??
                request?.timestamp ??
                '';
            const submittedAt = normalizeSubmittedAt(submittedAtRaw);
            const dateDisplay = submittedAt
                ? submittedAt.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
                : 'Date unavailable';
            const createdOnDisplay = submittedAt
                ? submittedAt.toLocaleString()
                : 'Not provided';
            const updatedAtDisplay = request?.updatedAt
                ? normalizeSubmittedAt(request.updatedAt)?.toLocaleString() ?? 'Not provided'
                : 'Not provided';
            const statusLabel = formatStatusForDisplay(
                request?.state ?? request?.status,
                'Not provided'
            );
            const requestType = request?.type ?? 'Not provided';
            const requestGroupUuid = request?.groupUuid;
            const requestRoles = Array.isArray(request?.roles)
                ? request.roles
                : request?.role
                    ? [request.role]
                    : request?.roleName
                        ? [request.roleName]
                        : [];
            const resolveGroupName = (group) => {
                if (!group) {
                    return '';
                }

                const groupId = normalizeGroupId(group);
                const fallbackGroupName = firstResolvedDisplayName(
                    [group?.name, group?.displayName],
                    ''
                );

                if (!groupId) {
                    return fallbackGroupName;
                }

                return supervisorGroupLookup.get(groupId) ?? fallbackGroupName ?? groupId;
            };
            const resolveRoleName = (role) => {
                if (!role) {
                    return '';
                }

                if (typeof role === 'string' || typeof role === 'number') {
                    return String(role).trim();
                }

                return firstResolvedDisplayName(
                    [role?.name, role?.displayName, role?.id, role?.uuid],
                    ''
                );
            };
            const requestedGroupName = resolveGroupName(requestGroupUuid);
            const requestedRoleNames = requestRoles
                .map((role) => resolveRoleName(role))
                .filter(Boolean);
            const requestedEntitlementSummary = getRequestedEntitlementSummary(
                requestedGroupName,
                requestedRoleNames
            );
            const resolvedEntitlementName = requestedGroupName || requestedRoleNames[0] || 'Unknown';
            return {
                id: requestId,
                requestDisplayId,
                date: dateDisplay,
                createdOnDisplay,
                updatedAtDisplay,
                statusLabel,
                requestType,
                ...requestedEntitlementSummary,
                user: requesterDisplayName,
                requesterFirstName: resolvedRequesterFirstNameDisplay,
                requesterLastName: resolvedRequesterLastNameDisplay,
                requesterUsername: resolvedRequesterUsernameDisplay,
                requesterEmail: resolvedRequesterEmailDisplay,
                requesterJobTitle: resolvedRequesterJobTitle,
                requesterUserType: resolvedRequesterUserType,
                requesterContractNumber: resolvedRequesterContractNumber,
                requesterContractExpirationDate: resolvedRequesterContractExpirationDate,
                requesterPhotoUrl,
                group: resolvedEntitlementName,
                currentStep: 2,
                supervisorApprovalName: '',
                supervisorApprovalDate: '',
                supervisorComments: '',
                justification: request?.justification ?? 'Not provided',
                jobResponsibilities: request?.jobResponsibilities ?? 'Not provided',
                comments: false,
            };
        });
    }, [
        normalizeSubmittedAt,
        formatApprovalRequestId,
        sortedSupervisorApprovalRequests,
        supervisorGroupLookup,
        supervisorRequesterLookup,
        supervisorRequesterPhotoLookup,
    ]);

    const sortedEntitlementOwnerApprovalRequests = useMemo(() => {
        return [...entitlementOwnerApprovalRequests].sort(compareApprovalRequestsForDisplay);
    }, [compareApprovalRequestsForDisplay, entitlementOwnerApprovalRequests]);

    const entitlementOwnerApprovalTasks = useMemo(() => {
        return sortedEntitlementOwnerApprovalRequests.map((request, index) => {
            const requesterId = request?.requesterUuid ?? null;
            const supervisorId = request?.supervisorUuid ?? null;
            const supervisorReview = request?.supervisorReview ?? null;
            const resolvedRequester = requesterId
                ? entitlementOwnerRequesterLookup.get(String(requesterId))
                : null;
            const requesterPhotoUrl = requesterId
                ? entitlementOwnerRequesterPhotoLookup.get(String(requesterId))
                : null;
            const resolvedSupervisor = supervisorId
                ? entitlementOwnerSupervisorLookup.get(String(supervisorId))
                : null;
            const supervisorPhotoUrl = supervisorId
                ? entitlementOwnerSupervisorPhotoLookup.get(String(supervisorId))
                : null;
            const resolvedRequesterFirstName = resolvedRequester?.firstName ?? '';
            const resolvedRequesterLastName = resolvedRequester?.lastName ?? '';
            const resolvedRequesterUsername =
                resolvedRequester?.preferred_username ?? resolvedRequester?.username ?? '';
            const resolvedRequesterEmail = resolvedRequester?.email ?? '';
            const resolvedRequesterDisplayName =
                resolvedRequester?.name ??
                resolvedRequester?.displayName ??
                `${resolvedRequesterFirstName} ${resolvedRequesterLastName}`.trim() ??
                resolvedRequesterUsername ??
                resolvedRequesterEmail ??
                '';
            const requesterDisplayName =
                resolvedRequesterDisplayName ||
                'Unknown user';
            const resolvedRequesterFirstNameDisplay =
                resolvedRequesterFirstName || 'Not provided';
            const resolvedRequesterLastNameDisplay =
                resolvedRequesterLastName || 'Not provided';
            const resolvedRequesterUsernameDisplay =
                resolvedRequesterUsername || 'Not provided';
            const resolvedRequesterEmailDisplay =
                resolvedRequesterEmail || 'Not provided';
            const resolvedRequesterJobTitle = resolveRequesterProfileField(
                resolvedRequester,
                ['jobTitle', 'JobTitle'],
                [
                    resolvedRequester?.jobTitle,
                    resolvedRequester?.job_title,
                    request?.requesterJobTitle,
                    request?.jobTitle,
                ]
            );
            const resolvedRequesterUserType = getUserTypeDisplayLabel(
                resolveRequesterProfileField(
                    resolvedRequester,
                    ['UserType', 'userType', 'user_type'],
                    [
                        resolvedRequester?.userType,
                        resolvedRequester?.UserType,
                        resolvedRequester?.user_type,
                        request?.requesterUserType,
                        request?.requesterUsertype,
                        request?.userType,
                        request?.UserType,
                    ]
                ),
                ''
            );
            const resolvedRequesterContractNumber = resolveRequesterProfileField(
                resolvedRequester,
                ['contractNumber', 'ContractNumber'],
                [
                    resolvedRequester?.contractNumber,
                    resolvedRequester?.contract_number,
                    request?.requesterContractNumber,
                    request?.contractNumber,
                ]
            );
            const resolvedRequesterContractExpirationDate = resolveRequesterProfileField(
                resolvedRequester,
                ['contractExpirationDate', 'ContractExpirationDate'],
                [
                    resolvedRequester?.contractExpirationDate,
                    resolvedRequester?.contract_expiration_date,
                    request?.requesterContractExpirationDate,
                    request?.contractExpirationDate,
                ]
            );
            const resolvedSupervisorFirstName = resolvedSupervisor?.firstName ?? '';
            const resolvedSupervisorLastName = resolvedSupervisor?.lastName ?? '';
            const resolvedSupervisorUsername =
                resolvedSupervisor?.preferred_username ?? resolvedSupervisor?.username ?? '';
            const resolvedSupervisorEmail = resolvedSupervisor?.email ?? '';
            const supervisorDisplayName =
                firstResolvedDisplayName([
                    resolvedSupervisor?.name,
                    resolvedSupervisor?.displayName,
                    `${resolvedSupervisorFirstName} ${resolvedSupervisorLastName}`.trim(),
                    resolvedSupervisorUsername,
                ], '');
            const requestId = request?.id ?? `ENT-${index + 1}`;
            const requestDisplayId = formatApprovalRequestId(request, index + 1);
            const supervisorDecisionRaw = supervisorReview?.decision ?? '';
            const supervisorDecisionDateRaw = supervisorReview?.createdAt ?? '';
            const supervisorDecisionDate = normalizeSubmittedAt(supervisorDecisionDateRaw);
            const supervisorDecisionDateDisplay = supervisorDecisionDate
                ? supervisorDecisionDate.toLocaleString()
                : 'Not provided';
            const submittedAtRaw = request?.createdAt ?? '';
            const submittedAt = normalizeSubmittedAt(submittedAtRaw);
            const dateDisplay = submittedAt
                ? submittedAt.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
                : 'Date unavailable';
            const createdOnDisplay = submittedAt
                ? submittedAt.toLocaleString()
                : 'Not provided';
            const updatedAtDisplay = request?.updatedAt
                ? normalizeSubmittedAt(request.updatedAt)?.toLocaleString() ?? 'Not provided'
                : 'Not provided';
            const statusLabel = formatStatusForDisplay(request?.state, 'Not provided');
            const requestType = request?.type ?? 'Not provided';
            const requestGroupUuid = request.groupUuid;
            const requestRoles = Array.isArray(request?.roles) ? request.roles : [];
            const resolveGroupName = (group) => {
                if (!group) {
                    return '';
                }

                const groupId = normalizeGroupId(group);
                const fallbackGroupName = firstResolvedDisplayName(
                    [group?.name, group?.displayName],
                    ''
                );

                if (!groupId) {
                    return fallbackGroupName;
                }

                return entitlementOwnerGroupLookup.get(groupId) ?? fallbackGroupName ?? groupId;
            };
            const resolveRoleName = (role) => {
                if (!role) {
                    return '';
                }

                if (typeof role === 'string' || typeof role === 'number') {
                    return String(role).trim();
                }

                return firstResolvedDisplayName(
                    [role?.name, role?.displayName, role?.id, role?.uuid],
                    ''
                );
            };
            const requestedGroupName = resolveGroupName(requestGroupUuid);
            const requestedRoleNames = requestRoles
                .map((role) => resolveRoleName(role))
                .filter(Boolean);
            const requestedEntitlementSummary = getRequestedEntitlementSummary(
                requestedGroupName,
                requestedRoleNames
            );
            const resolvedEntitlementName = requestedGroupName || requestedRoleNames[0] || 'Unknown';
            return {
                id: requestId,
                requestDisplayId,
                date: dateDisplay,
                createdOnDisplay,
                updatedAtDisplay,
                statusLabel,
                requestType,
                ...requestedEntitlementSummary,
                user: requesterDisplayName,
                requesterFirstName: resolvedRequesterFirstNameDisplay,
                requesterLastName: resolvedRequesterLastNameDisplay,
                requesterUsername: resolvedRequesterUsernameDisplay,
                requesterEmail: resolvedRequesterEmailDisplay,
                requesterJobTitle: resolvedRequesterJobTitle,
                requesterUserType: resolvedRequesterUserType,
                requesterContractNumber: resolvedRequesterContractNumber,
                requesterContractExpirationDate: resolvedRequesterContractExpirationDate,
                requesterPhotoUrl,
                group: resolvedEntitlementName,
                currentStep: 3,
                supervisorApprovalDecision: supervisorDecisionRaw || 'Not provided',
                supervisorApprovalDecisionDate: supervisorDecisionDateDisplay,
                supervisorName: supervisorDisplayName || 'Not provided',
                supervisorEmail: resolvedSupervisorEmail || 'Not provided',
                supervisorPhotoUrl,
                supervisorComments: supervisorReview?.notes ?? '',
                hasSupervisorReview: Boolean(supervisorReview),
                justification: request?.justification ?? 'Not provided',
                jobResponsibilities: request?.jobResponsibilities ?? 'Not provided',
                comments: false,
            };
        });
    }, [
        normalizeSubmittedAt,
        formatApprovalRequestId,
        sortedEntitlementOwnerApprovalRequests,
        entitlementOwnerGroupLookup,
        entitlementOwnerRequesterLookup,
        entitlementOwnerRequesterPhotoLookup,
        entitlementOwnerSupervisorLookup,
        entitlementOwnerSupervisorPhotoLookup,
    ]);

    const handleSelectAllSupervisorApprovals = () => {
        setSelectedSupervisorApprovalIds(new Set(supervisorApprovalTasks.map((approval) => approval.id)));
    };

    const handleUnselectAllSupervisorApprovals = () => {
        setSelectedSupervisorApprovalIds(new Set());
    };

    const submitSupervisorApprovals = (approvalIds, decision) => {
        const approvalIdSet =
            approvalIds instanceof Set ? approvalIds : new Set(approvalIds ?? []);
        const selectedNames = supervisorApprovalTasks
            .filter((approval) => approvalIdSet.has(approval.id))
            .map((approval) => approval.user)
            .join(', ');
        const isRejection = decision === 'DENIED';
        submitReviewRequests({
            approvalIds: approvalIdSet,
            decision,
            notesLookup: supervisorApprovalComments,
            setError: setSupervisorApprovalActionError,
            setSubmitting: setIsSubmittingSupervisorAction,
            setSuccessMessage: setSupervisorApprovalSuccessMessage,
            onSuccess: (ids) => {
                setSelectedSupervisorApprovalIds(new Set());
                const successMessage = selectedNames
                    ? `Supervisor ${isRejection ? 'rejection' : 'approval'} submitted for: ${selectedNames}`
                    : `Supervisor ${isRejection ? 'rejection' : 'approval'} submitted.`;
                window.sessionStorage.setItem('supervisorApprovalSuccessMessage', successMessage);
                window.location.reload();
                setSupervisorApprovalComments((prev) => {
                    const next = { ...prev };
                    ids.forEach((id) => {
                        delete next[id];
                    });
                    return next;
                });
            },
            reviewType: 'SUPERVISOR',
        });
    };

    const handleApproveSupervisorApprovals = () => {
        submitSupervisorApprovals(selectedSupervisorApprovalIds, 'APPROVED');
    };

    const handleRejectSupervisorApprovals = () => {
        submitSupervisorApprovals(selectedSupervisorApprovalIds, 'DENIED');
    };

    const handleApproveSingleSupervisorApproval = (approvalId) => {
        submitSupervisorApprovals(new Set([approvalId]), 'APPROVED');
    };

    const handleRejectSingleSupervisorApproval = (approvalId) => {
        submitSupervisorApprovals(new Set([approvalId]), 'DENIED');
    };

    const handleSelectAllEntitlementOwnerApprovals = () => {
        setSelectedEntitlementOwnerApprovalIds(
            new Set(entitlementOwnerApprovalTasks.map((approval) => approval.id))
        );
    };

    const handleUnselectAllEntitlementOwnerApprovals = () => {
        setSelectedEntitlementOwnerApprovalIds(new Set());
    };

    const submitEntitlementOwnerApprovals = (approvalIds, decision) => {
        const approvalIdSet =
            approvalIds instanceof Set ? approvalIds : new Set(approvalIds ?? []);
        const selectedNames = entitlementOwnerApprovalTasks
            .filter((approval) => approvalIdSet.has(approval.id))
            .map((approval) => approval.user)
            .join(', ');
        const isRejection = decision === 'DENIED';
        submitReviewRequests({
            approvalIds: approvalIdSet,
            decision,
            notesLookup: entitlementOwnerApprovalComments,
            setError: setEntitlementOwnerApprovalActionError,
            setSubmitting: setIsSubmittingEntitlementOwnerAction,
            setSuccessMessage: setEntitlementOwnerApprovalSuccessMessage,
            onSuccess: (ids) => {
                setSelectedEntitlementOwnerApprovalIds(new Set());
                const successMessage = selectedNames
                    ? `Entitlement owner ${isRejection ? 'rejection' : 'approval'} submitted for: ${selectedNames}`
                    : `Entitlement owner ${isRejection ? 'rejection' : 'approval'} submitted.`;
                window.sessionStorage.setItem(
                    'entitlementOwnerApprovalSuccessMessage',
                    successMessage
                );
                window.location.reload();
                setEntitlementOwnerApprovalComments((prev) => {
                    const next = { ...prev };
                    ids.forEach((id) => {
                        delete next[id];
                    });
                    return next;
                });
            },
            reviewType: 'ENTITLEMENT_OWNER',
        });
    };

    const handleApproveEntitlementOwnerApprovals = () => {
        submitEntitlementOwnerApprovals(selectedEntitlementOwnerApprovalIds, 'APPROVED');
    };

    const handleRejectEntitlementOwnerApprovals = () => {
        submitEntitlementOwnerApprovals(selectedEntitlementOwnerApprovalIds, 'DENIED');
    };

    const handleApproveSingleEntitlementOwnerApproval = (approvalId) => {
        submitEntitlementOwnerApprovals(new Set([approvalId]), 'APPROVED');
    };

    const handleRejectSingleEntitlementOwnerApproval = (approvalId) => {
        submitEntitlementOwnerApprovals(new Set([approvalId]), 'DENIED');
    };

    return (
        <AppLayout sidebar authDetails={authDetails} onSignOut={onSignOut}>
            <section className="usa-section">
                <div className="grid-container">
                    <div className="grid-row grid-gap">
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Your Direct Reports</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {isLoadingSupervisorReports ? (
                                            <ul className="usa-list usa-list--unstyled approval-direct-report-list" aria-hidden="true">
                                                {Array.from({ length: 3 }).map((_, index) => (
                                                    <li
                                                        key={`approval-direct-report-loading-${index}`}
                                                        className="approval-direct-report"
                                                    >
                                                        <div className="approval-requester__avatar approval-requester__avatar--report" />
                                                        <p className="approval-direct-report__name">
                                                            <ValueWithSkeleton loading width="14ch" />
                                                        </p>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : supervisorReportsError ? (
                                            <p className="usa-prose usa-error-message">{supervisorReportsError}</p>
                                        ) : supervisorReports.length > 0 ? (
                                            <ul className="usa-list usa-list--unstyled approval-direct-report-list">
                                                {supervisorReports.map((report, index) => {
                                                    const reportId = report.id ?? report.uuid ?? report.username ?? report.email;
                                                    const reportRouteId =
                                                        report.uuid ??
                                                        report.id ??
                                                        report.userId ??
                                                        report.user_id ??
                                                        report.username ??
                                                        report.email ??
                                                        '';
                                                    const reportName =
                                                        report.name ??
                                                        `${report.firstName ?? ''} ${report.lastName ?? ''}`.trim() ??
                                                        report.displayName ??
                                                        report.username ??
                                                        report.email ??
                                                        reportId ??
                                                        'Unknown user';
                                                    const reportEmail = report.email ?? report.username ?? '';
                                                    const reportPhotoKey =
                                                        report.uuid ?? report.id ?? report.userId ?? report.user_id ?? null;
                                                    const reportPhotoUrl = reportPhotoKey
                                                        ? supervisorReportPhotoLookup.get(String(reportPhotoKey))
                                                        : '';
                                                    return (
                                                        <li
                                                            key={reportId ?? reportName ?? `direct-report-${index + 1}`}
                                                            className="approval-direct-report"
                                                        >
                                                            <div
                                                                className={
                                                                    reportPhotoUrl
                                                                        ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--report'
                                                                        : 'approval-requester__avatar approval-requester__avatar--report'
                                                                }
                                                                aria-hidden={!reportPhotoUrl}
                                                            >
                                                                {reportPhotoUrl ? (
                                                                    <img
                                                                        className="approval-requester__avatar-image"
                                                                        src={reportPhotoUrl}
                                                                        alt={reportName || 'Direct report'}
                                                                    />
                                                                ) : null}
                                                            </div>
                                                            {String(reportRouteId || '').trim() ? (
                                                                <Link
                                                                    className="approval-direct-report__name approval-direct-report__name-link"
                                                                    to={`/users/${encodeURIComponent(String(reportRouteId).trim())}`}
                                                                >
                                                                    <ValueWithSkeleton value={reportName} width="14ch" />
                                                                </Link>
                                                            ) : (
                                                                <p className="approval-direct-report__name">
                                                                    <ValueWithSkeleton value={reportName} width="14ch" />
                                                                </p>
                                                            )}
                                                            {reportEmail ? (
                                                                <CopyableEmail
                                                                    email={reportEmail}
                                                                    currentUserEmail={currentUserEmail}
                                                                    fallback={null}
                                                                    className="approval-email-line approval-direct-report__email copyable-email"
                                                                />
                                                            ) : null}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className="usa-prose">
                                                No direct reports are listed for your account.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">Your approval groups</h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {approverGroupPhotoUploadError ? (
                                            <div className="usa-alert usa-alert--error" role="alert">
                                                <div className="usa-alert__body">
                                                    <p className="usa-alert__text">{approverGroupPhotoUploadError}</p>
                                                </div>
                                            </div>
                                        ) : null}
                                        <input
                                            ref={approverGroupPhotoInputRef}
                                            type="file"
                                            accept="image/*"
                                            aria-label="Upload approval group photo"
                                            className="profile-card__photo-input"
                                            onChange={handleApproverGroupPhotoChange}
                                        />
                                        {isLoadingApproverGroups ? (
                                            <ul className="usa-list usa-list--unstyled approval-group-list" aria-hidden="true">
                                                {Array.from({ length: 2 }).map((_, index) => (
                                                    <li
                                                        key={`approval-group-loading-${index}`}
                                                        className="approval-group-list__item approval-group-list__item--card"
                                                    >
                                                        <div className="approval-requester__avatar approval-requester__avatar--group" />
                                                        <ValueWithSkeleton loading type="chip" width="18ch" />
                                                        <ValueWithSkeleton loading width="4ch" />
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : approverGroups.length > 0 ? (
                                            <ul className="usa-list usa-list--unstyled approval-group-list">
                                                {approverGroups.map((group) => {
                                                    const groupId = String(group?.id ?? '').trim();
                                                    const groupPhotoUrl = groupId
                                                        ? approverGroupPhotoLookup.get(groupId)
                                                        : '';
                                                    const isUploading = isUploadingApproverGroupPhotoId === groupId;

                                                    return (
                                                        <li
                                                            key={group.id || group.name}
                                                            className="approval-group-list__item approval-group-list__item--card"
                                                        >
                                                            {groupId ? (
                                                                <Link
                                                                    className="approval-group-list__details-link"
                                                                    to={`/ApprovalGroupDetails/${encodeURIComponent(groupId)}`}
                                                                    aria-label={`View details for ${group.name}`}
                                                                >
                                                                    <div
                                                                        className={
                                                                            groupPhotoUrl
                                                                                ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--group'
                                                                                : 'approval-requester__avatar approval-requester__avatar--group'
                                                                        }
                                                                        aria-hidden={!groupPhotoUrl}
                                                                    >
                                                                        {groupPhotoUrl ? (
                                                                            <img
                                                                                className="approval-requester__avatar-image"
                                                                                src={groupPhotoUrl}
                                                                                alt={`${group.name} group`}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                    <span className="usa-tag">{group.name}</span>
                                                                </Link>
                                                            ) : (
                                                                <>
                                                                    <div
                                                                        className={
                                                                            groupPhotoUrl
                                                                                ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--group'
                                                                                : 'approval-requester__avatar approval-requester__avatar--group'
                                                                        }
                                                                        aria-hidden={!groupPhotoUrl}
                                                                    >
                                                                        {groupPhotoUrl ? (
                                                                            <img
                                                                                className="approval-requester__avatar-image"
                                                                                src={groupPhotoUrl}
                                                                                alt={`${group.name} group`}
                                                                            />
                                                                        ) : null}
                                                                    </div>
                                                                    <span className="usa-tag">{group.name}</span>
                                                                </>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="approval-group-list__photo-edit"
                                                                onClick={() => handleApproverGroupPhotoUploadClick(groupId)}
                                                                aria-label={`Update photo for ${group.name}`}
                                                                disabled={!groupId || Boolean(isUploadingApproverGroupPhotoId)}
                                                            >
                                                                <img
                                                                    src="/icons/edit.png"
                                                                    alt=""
                                                                    aria-hidden="true"
                                                                    className="approval-group-list__photo-edit-icon"
                                                                />
                                                                {isUploading ? 'Uploading...' : 'Edit'}
                                                            </button>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        ) : (
                                            <p className="usa-prose">
                                                No approver groups are listed for your account.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">
                                            Supervisor Approval tasks ({supervisorApprovalTasks.length})
                                        </h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {isLoadingSupervisorApprovals ? (
                                            <div className="usa-form" aria-hidden="true">
                                                <div className="usa-button-group">
                                                    <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    <ValueWithSkeleton loading type="chip" width="7ch" />
                                                    <ValueWithSkeleton loading type="chip" width="7ch" />
                                                </div>
                                                <fieldset className="usa-fieldset">
                                                    {Array.from({ length: 2 }).map((_, index) => (
                                                        <div key={`supervisor-approval-loading-${index}`} className="usa-checkbox">
                                                            <input
                                                                className="usa-checkbox__input usa-checkbox__input--tile"
                                                                type="checkbox"
                                                                disabled
                                                            />
                                                            <label className="usa-checkbox__label">
                                                                <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                <span className="usa-checkbox__label-description">
                                                                    <ValueWithSkeleton loading type="sentence" width="24ch" />
                                                                </span>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </fieldset>
                                            </div>
                                        ) : supervisorApprovalError ? (
                                            <p className="usa-prose usa-error-message">{supervisorApprovalError}</p>
                                        ) : (
                                            <>
                                                {supervisorApprovalSuccessMessage ? (
                                                    <div className="usa-alert usa-alert--success" role="status">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">
                                                                {supervisorApprovalSuccessMessage}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {supervisorApprovalTasks.length === 0 ? (
                                                    <p className="usa-prose">No supervisor approval tasks are pending.</p>
                                                ) : (
                                                    <div className="usa-form">
                                                <div className="usa-button-group">
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--outline"
                                                        onClick={handleSelectAllSupervisorApprovals}
                                                    >
                                                        Select all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--outline"
                                                        onClick={handleUnselectAllSupervisorApprovals}
                                                        disabled={
                                                            selectedSupervisorApprovalIds.size === 0 ||
                                                            isSubmittingSupervisorAction
                                                        }
                                                    >
                                                        Unselect all
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--approve"
                                                        onClick={handleApproveSupervisorApprovals}
                                                        disabled={
                                                            selectedSupervisorApprovalIds.size === 0 ||
                                                            isSubmittingSupervisorAction
                                                        }
                                                    >
                                                        Approve
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="usa-button usa-button--reject"
                                                        onClick={handleRejectSupervisorApprovals}
                                                        disabled={
                                                            selectedSupervisorApprovalIds.size === 0 ||
                                                            isSubmittingSupervisorAction
                                                        }
                                                    >
                                                        Reject
                                                    </button>
                                                </div>
                                                {supervisorApprovalActionError ? (
                                                    <div className="usa-alert usa-alert--error" role="alert">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">
                                                                {supervisorApprovalActionError}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                <fieldset className="usa-fieldset">
                                                    {supervisorApprovalTasks.map((approval) => (
                                                        <div key={approval.id} className="usa-checkbox">
                                                            <input
                                                                className="usa-checkbox__input usa-checkbox__input--tile"
                                                                id={`supervisor-approval-${approval.id}`}
                                                                type="checkbox"
                                                                name="supervisor-approval-tasks"
                                                                checked={selectedSupervisorApprovalIds.has(approval.id)}
                                                                onChange={() => toggleSupervisorApprovalSelection(approval.id)}
                                                                disabled={isSubmittingSupervisorAction}
                                                            />
                                                            <label
                                                                className="usa-checkbox__label"
                                                                htmlFor={`supervisor-approval-${approval.id}`}
                                                            >
                                                                <span className="usa-checkbox__label-description approval-title">
                                                                        <span className="approval-title__meta">
                                                                            <span>
                                                                                <ValueWithSkeleton value={approval.requestDisplayId} width="9ch" /> ·{' '}
                                                                                <ValueWithSkeleton value={approval.date} width="11ch" /> ·{' '}
                                                                                <ValueWithSkeleton
                                                                                    value={approval.group}
                                                                                    loading={isLoadingSupervisorGroupLookup}
                                                                                    width="12ch"
                                                                                />{' '}
                                                                                ·{' '}
                                                                                <ValueWithSkeleton
                                                                                    value={approval.user}
                                                                                    loading={isLoadingSupervisorRequesterLookup}
                                                                                    width="12ch"
                                                                                />
                                                                            </span>
                                                                        {isApprovalOverdue(approval.date) ? (
                                                                            <span className="usa-tag usa-tag--overdue">Overdue</span>
                                                                        ) : null}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        className="approval-toggle"
                                                                        onClick={(event) => {
                                                                            event.preventDefault();
                                                                            toggleSupervisorApprovalDetails(approval.id);
                                                                        }}
                                                                        aria-expanded={expandedSupervisorApprovalIds.has(approval.id)}
                                                                        aria-controls={`supervisor-approval-details-${approval.id}`}
                                                                    >
                                                                        {expandedSupervisorApprovalIds.has(approval.id)
                                                                            ? 'Hide details'
                                                                            : 'Show details'}
                                                                    </button>
                                                                </span>
                                                                <div
                                                                    id={`supervisor-approval-details-${approval.id}`}
                                                                    className="approval-details"
                                                                    hidden={!expandedSupervisorApprovalIds.has(approval.id)}
                                                                >
                                                                    <span className="usa-checkbox__label-description">
                                                                        <div
                                                                            className="usa-step-indicator usa-step-indicator--counters approval-step-indicator"
                                                                            aria-label="Approval steps"
                                                                        >
                                                                            <ol className="usa-step-indicator__segments">
                                                                                {[
                                                                                    'Submitted',
                                                                                    'Supervisor approval',
                                                                                    'Entitlement owner approval',
                                                                                    'Implementation',
                                                                                ].map((label, index) => {
                                                                                    const stepNumber = index + 1;
                                                                                    const isComplete = stepNumber < approval.currentStep;
                                                                                    const isCurrent = stepNumber === approval.currentStep;
                                                                                    return (
                                                                                        <li
                                                                                            key={label}
                                                                                            className={
                                                                                                isComplete
                                                                                                    ? 'usa-step-indicator__segment usa-step-indicator__segment--complete'
                                                                                                    : isCurrent
                                                                                                        ? 'usa-step-indicator__segment usa-step-indicator__segment--current'
                                                                                                        : 'usa-step-indicator__segment'
                                                                                            }
                                                                                        >
                                                                                            <span className="usa-step-indicator__segment-label">
                                                                                                {label}
                                                                                            </span>
                                                                                        </li>
                                                                                    );
                                                                                })}
                                                                            </ol>
                                                                        </div>
                                                                    </span>
                                                                    <div className="usa-card approval-summary-card">
                                                                        <div className="usa-card__container">
                                                                            <div className="usa-card__body">
                                                                                <div className="approval-summary-grid">
                                                                                    <div className="approval-summary-column">
                                                                                        <div
                                                                                            className="usa-summary-box approval-summary-box"
                                                                                            role="region"
                                                                                            aria-labelledby={`supervisor-access-submission-${approval.id}`}
                                                                                        >
                                                                                            <div className="usa-summary-box__body">
                                                                                                <h4
                                                                                                    className="usa-summary-box__heading"
                                                                                                    id={`supervisor-access-submission-${approval.id}`}
                                                                                                >
                                                                                                    Submission Details
                                                                                                </h4>
                                                                                                <div className="usa-summary-box__text">
                                                                                                    <p>
                                                                                                        <span className="approval-key">Created On</span>
                                                                                                        <ValueWithSkeleton value={approval.createdOnDisplay} />
                                                                                                    </p>
                                                                                                    <p>
                                                                                                        <span className="approval-key">Status</span>
                                                                                                        <ValueWithSkeleton value={approval.statusLabel} width="12ch" />
                                                                                                    </p>
                                                                                                    <p>
                                                                                                        <span className="approval-key">Last updated</span>
                                                                                                        <ValueWithSkeleton value={approval.updatedAtDisplay} />
                                                                                                    </p>
                                                                                                    <p>
                                                                                                        <span className="approval-key">Request type</span>
                                                                                                        <ValueWithSkeleton value={approval.requestType} width="12ch" />
                                                                                                    </p>
                                                                                                    <p>
                                                                                                        <span className="approval-key">
                                                                                                            {approval.requestedEntitlementLabel}
                                                                                                        </span>
                                                                                                        <ValueWithSkeleton
                                                                                                            value={approval.requestedEntitlementDisplay}
                                                                                                            loading={isLoadingSupervisorGroupLookup}
                                                                                                            type="sentence"
                                                                                                        />
                                                                                                    </p>
                                                                                                    <p>
                                                                                                        <span className="approval-key">
                                                                                                            Job Responsibilities
                                                                                                        </span>
                                                                                                        <ValueWithSkeleton
                                                                                                            value={approval.jobResponsibilities}
                                                                                                            type="sentence"
                                                                                                        />
                                                                                                    </p>
                                                                                                    <p>
                                                                                                        <span className="approval-key">
                                                                                                            Justification provided
                                                                                                        </span>
                                                                                                        <ValueWithSkeleton
                                                                                                            value={approval.justification}
                                                                                                            type="sentence"
                                                                                                        />
                                                                                                    </p>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                        {approval.currentStep > 2 &&
                                                                                        approval.supervisorApprovalName ? (
                                                                                            <div
                                                                                                className="usa-summary-box approval-summary-box"
                                                                                                role="region"
                                                                                                aria-labelledby={`supervisor-approval-${approval.id}`}
                                                                                            >
                                                                                                <div className="usa-summary-box__body">
                                                                                                    <h4
                                                                                                        className="usa-summary-box__heading"
                                                                                                        id={`supervisor-approval-${approval.id}`}
                                                                                                    >
                                                                                                        Supervisor Approval Details
                                                                                                    </h4>
                                                                                                    <div className="usa-summary-box__text">
                                                                                                        <p>
                                                                                                            <span className="approval-key">
                                                                                                                Supervisor approval
                                                                                                            </span>
                                                                                                            {approval.supervisorApprovalName}
                                                                                                            {approval.supervisorApprovalDate
                                                                                                                ? ` · ${approval.supervisorApprovalDate}`
                                                                                                                : ''}
                                                                                                        </p>
                                                                                                        <p>
                                                                                                            <span className="approval-key">
                                                                                                                Supervisor comments
                                                                                                            </span>
                                                                                                            <ValueWithSkeleton
                                                                                                                value={approval.supervisorComments || 'None provided.'}
                                                                                                            />
                                                                                                        </p>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        ) : null}
                                                                                    </div>
                                                                                    <div className="approval-summary-column">
                                                                                        <div
                                                                                            className="usa-summary-box approval-summary-box"
                                                                                            role="region"
                                                                                            aria-labelledby={`supervisor-requester-details-${approval.id}`}
                                                                                        >
                                                                                            <div className="usa-summary-box__body">
                                                                                                <h4
                                                                                                    className="usa-summary-box__heading"
                                                                                                    id={`supervisor-requester-details-${approval.id}`}
                                                                                                >
                                                                                                    Requester Details
                                                                                                </h4>
                                                                                                <div className="usa-summary-box__text">
                                                                                                    <div className="approval-requester">
                                                                                                        <div
                                                                                                            className={
                                                                                                                approval.requesterPhotoUrl
                                                                                                                    ? 'approval-requester__avatar approval-requester__avatar--photo'
                                                                                                                    : 'approval-requester__avatar'
                                                                                                            }
                                                                                                            aria-hidden={!approval.requesterPhotoUrl}
                                                                                                        >
                                                                                                            {approval.requesterPhotoUrl ? (
                                                                                                                <img
                                                                                                                    className="approval-requester__avatar-image"
                                                                                                                    src={approval.requesterPhotoUrl}
                                                                                                                    alt={`${approval.requesterFirstName} ${approval.requesterLastName}`.trim() || 'Requester'}
                                                                                                                />
                                                                                                            ) : null}
                                                                                                        </div>
                                                                                                        <div className="approval-requester__info">
                                                                                                            <p>
                                                                                                                <span className="approval-key">
                                                                                                                    First name
                                                                                                                </span>
                                                                                                                <ValueWithSkeleton
                                                                                                                    value={approval.requesterFirstName}
                                                                                                                    loading={isLoadingSupervisorRequesterLookup}
                                                                                                                    width="11ch"
                                                                                                                />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">
                                                                                                                    Last name
                                                                                                                </span>
                                                                                                                <ValueWithSkeleton
                                                                                                                    value={approval.requesterLastName}
                                                                                                                    loading={isLoadingSupervisorRequesterLookup}
                                                                                                                    width="11ch"
                                                                                                                />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">
                                                                                                                    Username
                                                                                                                </span>
                                                                                                                <ValueWithSkeleton
                                                                                                                    value={approval.requesterUsername}
                                                                                                                    loading={isLoadingSupervisorRequesterLookup}
                                                                                                                    width="11ch"
                                                                                                                />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">Email</span>
                                                                                                                {isLoadingSupervisorRequesterLookup ? (
                                                                                                                    <ValueWithSkeleton loading width="20ch" />
                                                                                                                ) : (
                                                                                                                    <CopyableEmail
                                                                                                                        email={approval.requesterEmail}
                                                                                                                        currentUserEmail={currentUserEmail}
                                                                                                                    />
                                                                                                                )}
                                                                                                            </p>
                                                                                                            {isLoadingSupervisorRequesterLookup || approval.requesterJobTitle ? (
                                                                                                                <p>
                                                                                                                    <span className="approval-key">Job title</span>
                                                                                                                    <ValueWithSkeleton
                                                                                                                        value={approval.requesterJobTitle}
                                                                                                                        loading={isLoadingSupervisorRequesterLookup}
                                                                                                                        width="14ch"
                                                                                                                    />
                                                                                                                </p>
                                                                                                            ) : null}
                                                                                                            {isLoadingSupervisorRequesterLookup || approval.requesterUserType ? (
                                                                                                                <p>
                                                                                                                    <span className="approval-key">User type</span>
                                                                                                                    <ValueWithSkeleton
                                                                                                                        value={approval.requesterUserType}
                                                                                                                        loading={isLoadingSupervisorRequesterLookup}
                                                                                                                        width="12ch"
                                                                                                                    />
                                                                                                                </p>
                                                                                                            ) : null}
                                                                                                            {isLoadingSupervisorRequesterLookup || approval.requesterContractNumber ? (
                                                                                                                <p>
                                                                                                                    <span className="approval-key">Contract number</span>
                                                                                                                    <ValueWithSkeleton
                                                                                                                        value={approval.requesterContractNumber}
                                                                                                                        loading={isLoadingSupervisorRequesterLookup}
                                                                                                                        width="12ch"
                                                                                                                    />
                                                                                                                </p>
                                                                                                            ) : null}
                                                                                                            {isLoadingSupervisorRequesterLookup || approval.requesterContractExpirationDate ? (
                                                                                                                <p>
                                                                                                                    <span className="approval-key">
                                                                                                                        Contract expiration date
                                                                                                                    </span>
                                                                                                                    <ValueWithSkeleton
                                                                                                                        value={approval.requesterContractExpirationDate}
                                                                                                                        loading={isLoadingSupervisorRequesterLookup}
                                                                                                                        width="12ch"
                                                                                                                    />
                                                                                                                </p>
                                                                                                            ) : null}
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <span className="usa-checkbox__label-description">
                                                                        <label
                                                                            className="usa-label approval-comments-label"
                                                                            htmlFor={`supervisor-approval-comments-${approval.id}`}
                                                                        >
                                                                            Approver Comments (Required for a rejection)
                                                                        </label>
                                                                        <textarea
                                                                            className="usa-textarea"
                                                                            id={`supervisor-approval-comments-${approval.id}`}
                                                                            name={`supervisor-approval-comments-${approval.id}`}
                                                                            rows="3"
                                                                            placeholder="Add comments for this approval"
                                                                            value={supervisorApprovalComments[approval.id] ?? ''}
                                                                            onChange={(event) =>
                                                                                updateSupervisorApprovalComment(
                                                                                    approval.id,
                                                                                    event.target.value
                                                                                )
                                                                            }
                                                                            disabled={isSubmittingSupervisorAction}
                                                                        />
                                                                        <div className="approval-actions">
                                                                            <button
                                                                                type="button"
                                                                                className="usa-button usa-button--approve"
                                                                                onClick={(event) => {
                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    handleApproveSingleSupervisorApproval(
                                                                                        approval.id
                                                                                    );
                                                                                }}
                                                                                disabled={isSubmittingSupervisorAction}
                                                                            >
                                                                                Approve request
                                                                            </button>
                                                                            <button
                                                                                type="button"
                                                                                className="usa-button usa-button--reject"
                                                                                onClick={(event) => {
                                                                                    event.preventDefault();
                                                                                    event.stopPropagation();
                                                                                    handleRejectSingleSupervisorApproval(
                                                                                        approval.id
                                                                                    );
                                                                                }}
                                                                                disabled={
                                                                                    isSubmittingSupervisorAction ||
                                                                                    !String(
                                                                                        supervisorApprovalComments[approval.id] ??
                                                                                            ''
                                                                                    ).trim()
                                                                                }
                                                                            >
                                                                                Reject request
                                                                            </button>
                                                                        </div>
                                                                    </span>
                                                                </div>
                                                            </label>
                                                        </div>
                                                    ))}
                                                        </fieldset>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tablet:grid-col-12">
                            <div className="usa-card">
                                <div className="usa-card__container">
                                    <div className="usa-card__header">
                                        <h2 className="usa-card__heading">
                                            Entitlement Owner Approval tasks ({entitlementOwnerApprovalTasks.length})
                                        </h2>
                                    </div>
                                    <div className="usa-card__body">
                                        {isLoadingEntitlementOwnerApprovals ? (
                                            <div className="usa-form" aria-hidden="true">
                                                <div className="usa-button-group">
                                                    <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    <ValueWithSkeleton loading type="chip" width="8ch" />
                                                    <ValueWithSkeleton loading type="chip" width="7ch" />
                                                    <ValueWithSkeleton loading type="chip" width="7ch" />
                                                </div>
                                                <fieldset className="usa-fieldset">
                                                    {Array.from({ length: 2 }).map((_, index) => (
                                                        <div key={`entitlement-owner-approval-loading-${index}`} className="usa-checkbox">
                                                            <input
                                                                className="usa-checkbox__input usa-checkbox__input--tile"
                                                                type="checkbox"
                                                                disabled
                                                            />
                                                            <label className="usa-checkbox__label">
                                                                <ValueWithSkeleton loading type="sentence" width="28ch" />
                                                                <span className="usa-checkbox__label-description">
                                                                    <ValueWithSkeleton loading type="sentence" width="24ch" />
                                                                </span>
                                                            </label>
                                                        </div>
                                                    ))}
                                                </fieldset>
                                            </div>
                                        ) : entitlementOwnerApprovalError ? (
                                            <p className="usa-prose usa-error-message">
                                                {entitlementOwnerApprovalError}
                                            </p>
                                        ) : (
                                            <>
                                                {entitlementOwnerApprovalSuccessMessage ? (
                                                    <div className="usa-alert usa-alert--success" role="status">
                                                        <div className="usa-alert__body">
                                                            <p className="usa-alert__text">
                                                                {entitlementOwnerApprovalSuccessMessage}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {entitlementOwnerApprovalTasks.length === 0 ? (
                                                    <p className="usa-prose">
                                                        No entitlement owner approval tasks are pending.
                                                    </p>
                                                ) : (
                                                    <div className="usa-form">
                                                        <div className="usa-button-group">
                                                            <button
                                                                type="button"
                                                                className="usa-button usa-button--outline"
                                                                onClick={
                                                                    handleSelectAllEntitlementOwnerApprovals
                                                                }
                                                            >
                                                                Select all
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="usa-button usa-button--outline"
                                                                onClick={
                                                                    handleUnselectAllEntitlementOwnerApprovals
                                                                }
                                                                disabled={
                                                                    selectedEntitlementOwnerApprovalIds.size === 0 ||
                                                                    isSubmittingEntitlementOwnerAction
                                                                }
                                                            >
                                                                Unselect all
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="usa-button usa-button--approve"
                                                                onClick={
                                                                    handleApproveEntitlementOwnerApprovals
                                                                }
                                                                disabled={
                                                                    selectedEntitlementOwnerApprovalIds.size === 0 ||
                                                                    isSubmittingEntitlementOwnerAction
                                                                }
                                                            >
                                                                Approve
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="usa-button usa-button--reject"
                                                                onClick={
                                                                    handleRejectEntitlementOwnerApprovals
                                                                }
                                                                disabled={
                                                                    selectedEntitlementOwnerApprovalIds.size === 0 ||
                                                                    isSubmittingEntitlementOwnerAction
                                                                }
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                        {entitlementOwnerApprovalActionError ? (
                                                            <div
                                                                className="usa-alert usa-alert--error"
                                                                role="alert"
                                                            >
                                                                <div className="usa-alert__body">
                                                                    <p className="usa-alert__text">
                                                                        {entitlementOwnerApprovalActionError}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        ) : null}
                                                        <fieldset className="usa-fieldset">
                                                            {entitlementOwnerApprovalTasks.map((approval) => (
                                                                <div key={approval.id} className="usa-checkbox">
                                                                    <input
                                                                        className="usa-checkbox__input usa-checkbox__input--tile"
                                                                        id={`entitlement-owner-approval-${approval.id}`}
                                                                        type="checkbox"
                                                                        name="entitlement-owner-approval-tasks"
                                                                        checked={selectedEntitlementOwnerApprovalIds.has(
                                                                            approval.id
                                                                        )}
                                                                        onChange={() =>
                                                                            toggleEntitlementOwnerApprovalSelection(
                                                                                approval.id
                                                                            )
                                                                        }
                                                                        disabled={isSubmittingEntitlementOwnerAction}
                                                                    />
                                                                    <label
                                                                        className="usa-checkbox__label"
                                                                        htmlFor={`entitlement-owner-approval-${approval.id}`}
                                                                    >
                                                                        <span className="usa-checkbox__label-description approval-title">
                                                                            <span className="approval-title__meta">
                                                                                <span>
                                                                                    <ValueWithSkeleton value={approval.requestDisplayId} width="9ch" /> ·{' '}
                                                                                    <ValueWithSkeleton value={approval.date} width="11ch" /> ·{' '}
                                                                                    <ValueWithSkeleton
                                                                                        value={approval.group}
                                                                                        loading={isLoadingEntitlementOwnerGroupLookup}
                                                                                        width="12ch"
                                                                                    />{' '}
                                                                                    ·{' '}
                                                                                    <ValueWithSkeleton
                                                                                        value={approval.user}
                                                                                        loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                        width="12ch"
                                                                                    />
                                                                                </span>
                                                                                {isApprovalOverdue(approval.date) ? (
                                                                                    <span className="usa-tag usa-tag--overdue">
                                                                                        Overdue
                                                                                    </span>
                                                                                ) : null}
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className="approval-toggle"
                                                                                onClick={(event) => {
                                                                                    event.preventDefault();
                                                                                    toggleEntitlementOwnerApprovalDetails(
                                                                                        approval.id
                                                                                    );
                                                                                }}
                                                                                aria-expanded={
                                                                                    expandedEntitlementOwnerApprovalIds.has(
                                                                                        approval.id
                                                                                    )
                                                                                }
                                                                                aria-controls={`entitlement-owner-approval-details-${approval.id}`}
                                                                            >
                                                                                {expandedEntitlementOwnerApprovalIds.has(
                                                                                    approval.id
                                                                                )
                                                                                    ? 'Hide details'
                                                                                    : 'Show details'}
                                                                            </button>
                                                                        </span>
                                                                        <div
                                                                            id={`entitlement-owner-approval-details-${approval.id}`}
                                                                            className="approval-details"
                                                                            hidden={!expandedEntitlementOwnerApprovalIds.has(
                                                                                approval.id
                                                                            )}
                                                                        >
                                                                            <span className="usa-checkbox__label-description">
                                                                                <div
                                                                                    className="usa-step-indicator usa-step-indicator--counters approval-step-indicator"
                                                                                    aria-label="Approval steps"
                                                                                >
                                                                                    <ol className="usa-step-indicator__segments">
                                                                                        {[
                                                                                            'Submitted',
                                                                                            'Supervisor approval',
                                                                                            'Entitlement owner approval',
                                                                                            'Implementation',
                                                                                        ].map((label, index) => {
                                                                                            const stepNumber = index + 1;
                                                                                            const isComplete =
                                                                                                stepNumber < approval.currentStep;
                                                                                            const isCurrent =
                                                                                                stepNumber === approval.currentStep;
                                                                                            return (
                                                                                                <li
                                                                                                    key={label}
                                                                                                    className={
                                                                                                        isComplete
                                                                                                            ? 'usa-step-indicator__segment usa-step-indicator__segment--complete'
                                                                                                            : isCurrent
                                                                                                                ? 'usa-step-indicator__segment usa-step-indicator__segment--current'
                                                                                                                : 'usa-step-indicator__segment'
                                                                                                    }
                                                                                                >
                                                                                                    <span className="usa-step-indicator__segment-label">
                                                                                                        {label}
                                                                                                    </span>
                                                                                                </li>
                                                                                            );
                                                                                        })}
                                                                                    </ol>
                                                                                </div>
                                                                            </span>
                                                                            <div className="usa-card approval-summary-card">
                                                                                <div className="usa-card__container">
                                                                                    <div className="usa-card__body">
                                                                                        <div className="approval-summary-grid">
                                                                                            <div className="approval-summary-column">
                                                                                                <div
                                                                                                    className="usa-summary-box approval-summary-box"
                                                                                                    role="region"
                                                                                                    aria-labelledby={`entitlement-owner-access-submission-${approval.id}`}
                                                                                                >
                                                                                                    <div className="usa-summary-box__body">
                                                                                                        <h4
                                                                                                            className="usa-summary-box__heading"
                                                                                                            id={`entitlement-owner-access-submission-${approval.id}`}
                                                                                                        >
                                                                                                            Submission Details
                                                                                                        </h4>
                                                                                                        <div className="usa-summary-box__text">
                                                                                                            <p>
                                                                                                                <span className="approval-key">Created On</span>
                                                                                                                <ValueWithSkeleton value={approval.createdOnDisplay} />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">Status</span>
                                                                                                                <ValueWithSkeleton value={approval.statusLabel} width="12ch" />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">Last updated</span>
                                                                                                                <ValueWithSkeleton value={approval.updatedAtDisplay} />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">Request type</span>
                                                                                                                <ValueWithSkeleton value={approval.requestType} width="12ch" />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">
                                                                                                                    {approval.requestedEntitlementLabel}
                                                                                                                </span>
                                                                                                                <ValueWithSkeleton
                                                                                                                    value={approval.requestedEntitlementDisplay}
                                                                                                                    loading={isLoadingEntitlementOwnerGroupLookup}
                                                                                                                    type="sentence"
                                                                                                                />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">
                                                                                                                    Job Responsibilities
                                                                                                                </span>
                                                                                                                <ValueWithSkeleton
                                                                                                                    value={approval.jobResponsibilities}
                                                                                                                    type="sentence"
                                                                                                                />
                                                                                                            </p>
                                                                                                            <p>
                                                                                                                <span className="approval-key">
                                                                                                                    Justification provided
                                                                                                                </span>
                                                                                                                <ValueWithSkeleton
                                                                                                                    value={approval.justification}
                                                                                                                    type="sentence"
                                                                                                                />
                                                                                                            </p>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            <div className="approval-summary-column">
                                                                                                <div
                                                                                                    className="usa-summary-box approval-summary-box"
                                                                                                    role="region"
                                                                                                    aria-labelledby={`entitlement-owner-requester-details-${approval.id}`}
                                                                                                >
                                                                                                    <div className="usa-summary-box__body">
                                                                                                        <h4
                                                                                                            className="usa-summary-box__heading"
                                                                                                            id={`entitlement-owner-requester-details-${approval.id}`}
                                                                                                        >
                                                                                                            Requester Details
                                                                                                        </h4>
                                                                                                        <div className="usa-summary-box__text">
                                                                                                            <div className="approval-requester">
                                                                                                                <div
                                                                                                                    className={
                                                                                                                        approval.requesterPhotoUrl
                                                                                                                            ? 'approval-requester__avatar approval-requester__avatar--photo'
                                                                                                                            : 'approval-requester__avatar'
                                                                                                                    }
                                                                                                                    aria-hidden={!approval.requesterPhotoUrl}
                                                                                                                >
                                                                                                                    {approval.requesterPhotoUrl ? (
                                                                                                                        <img
                                                                                                                            className="approval-requester__avatar-image"
                                                                                                                            src={approval.requesterPhotoUrl}
                                                                                                                            alt={`${approval.requesterFirstName} ${approval.requesterLastName}`.trim() || 'Requester'}
                                                                                                                        />
                                                                                                                    ) : null}
                                                                                                                </div>
                                                                                                                <div className="approval-requester__info">
                                                                                                                    <p>
                                                                                                                        <span className="approval-key">First name</span>
                                                                                                                        <ValueWithSkeleton
                                                                                                                            value={approval.requesterFirstName}
                                                                                                                            loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                            width="11ch"
                                                                                                                        />
                                                                                                                    </p>
                                                                                                                    <p>
                                                                                                                        <span className="approval-key">Last name</span>
                                                                                                                        <ValueWithSkeleton
                                                                                                                            value={approval.requesterLastName}
                                                                                                                            loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                            width="11ch"
                                                                                                                        />
                                                                                                                    </p>
                                                                                                                    <p>
                                                                                                                        <span className="approval-key">Username</span>
                                                                                                                        <ValueWithSkeleton
                                                                                                                            value={approval.requesterUsername}
                                                                                                                            loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                            width="11ch"
                                                                                                                        />
                                                                                                                    </p>
                                                                                                                    <p>
                                                                                                                        <span className="approval-key">Email</span>
                                                                                                                        {isLoadingEntitlementOwnerRequesterLookup ? (
                                                                                                                            <ValueWithSkeleton loading width="20ch" />
                                                                                                                        ) : (
                                                                                                                            <CopyableEmail
                                                                                                                                email={approval.requesterEmail}
                                                                                                                                currentUserEmail={currentUserEmail}
                                                                                                                            />
                                                                                                                        )}
                                                                                                                    </p>
                                                                                                                    {isLoadingEntitlementOwnerRequesterLookup || approval.requesterJobTitle ? (
                                                                                                                        <p>
                                                                                                                            <span className="approval-key">Job title</span>
                                                                                                                            <ValueWithSkeleton
                                                                                                                                value={approval.requesterJobTitle}
                                                                                                                                loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                                width="14ch"
                                                                                                                            />
                                                                                                                        </p>
                                                                                                                    ) : null}
                                                                                                                    {isLoadingEntitlementOwnerRequesterLookup || approval.requesterUserType ? (
                                                                                                                        <p>
                                                                                                                            <span className="approval-key">User type</span>
                                                                                                                            <ValueWithSkeleton
                                                                                                                                value={approval.requesterUserType}
                                                                                                                                loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                                width="12ch"
                                                                                                                            />
                                                                                                                        </p>
                                                                                                                    ) : null}
                                                                                                                    {isLoadingEntitlementOwnerRequesterLookup || approval.requesterContractNumber ? (
                                                                                                                        <p>
                                                                                                                            <span className="approval-key">Contract number</span>
                                                                                                                            <ValueWithSkeleton
                                                                                                                                value={approval.requesterContractNumber}
                                                                                                                                loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                                width="12ch"
                                                                                                                            />
                                                                                                                        </p>
                                                                                                                    ) : null}
                                                                                                                    {isLoadingEntitlementOwnerRequesterLookup || approval.requesterContractExpirationDate ? (
                                                                                                                        <p>
                                                                                                                            <span className="approval-key">
                                                                                                                                Contract expiration date
                                                                                                                            </span>
                                                                                                                            <ValueWithSkeleton
                                                                                                                                value={approval.requesterContractExpirationDate}
                                                                                                                                loading={isLoadingEntitlementOwnerRequesterLookup}
                                                                                                                                width="12ch"
                                                                                                                            />
                                                                                                                        </p>
                                                                                                                    ) : null}
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                            {approval.hasSupervisorReview ? (
                                                                                                <div className="approval-summary-column approval-summary-column--full">
                                                                                                    <div className="approval-summary-divider" />
                                                                                                    <div className="usa-card approval-supervisor-card">
                                                                                                        <div className="usa-card__container">
                                                                                                            <div className="usa-card__body">
                                                                                                                <h4
                                                                                                                    className="usa-summary-box__heading approval-supervisor-heading approval-summary-heading"
                                                                                                                    id={`entitlement-owner-supervisor-approval-${approval.id}`}
                                                                                                                >
                                                                                                                    Supervisor Approval Details
                                                                                                                    {approval.supervisorApprovalDecision === 'APPROVED' ? (
                                                                                                                        <span className="usa-tag">
                                                                                                                            {approval.supervisorApprovalDecisionDate
                                                                                                                                ? `APPROVED ON ${approval.supervisorApprovalDecisionDate}`
                                                                                                                                : 'APPROVED'}
                                                                                                                        </span>
                                                                                                                    ) : null}
                                                                                                                </h4>
                                                                                                                <div className="approval-supervisor-single">
                                                                                                                    <div className="approval-supervisor-identity">
                                                                                                                        <div
                                                                                                                            className={
                                                                                                                                approval.supervisorPhotoUrl
                                                                                                                                    ? 'approval-requester__avatar approval-requester__avatar--photo approval-requester__avatar--supervisor'
                                                                                                                                    : 'approval-requester__avatar approval-requester__avatar--supervisor'
                                                                                                                            }
                                                                                                                            aria-hidden={!approval.supervisorPhotoUrl}
                                                                                                                        >
                                                                                                                            {approval.supervisorPhotoUrl ? (
                                                                                                                                <img
                                                                                                                                    className="approval-requester__avatar-image"
                                                                                                                                    src={approval.supervisorPhotoUrl}
                                                                                                                                    alt={approval.supervisorName || 'Supervisor'}
                                                                                                                                />
                                                                                                                            ) : null}
                                                                                                                        </div>
                                                                                                                        <p className="approval-supervisor-name">
                                                                                                                            <ValueWithSkeleton
                                                                                                                                value={approval.supervisorName}
                                                                                                                                loading={isLoadingEntitlementOwnerSupervisorLookup}
                                                                                                                                width="13ch"
                                                                                                                            />
                                                                                                                        </p>
                                                                                                                        {isLoadingEntitlementOwnerSupervisorLookup ? (
                                                                                                                            <ValueWithSkeleton
                                                                                                                                loading
                                                                                                                                width="20ch"
                                                                                                                                className="approval-email-line"
                                                                                                                            />
                                                                                                                        ) : (
                                                                                                                            <CopyableEmail
                                                                                                                                email={approval.supervisorEmail}
                                                                                                                                currentUserEmail={currentUserEmail}
                                                                                                                                className="approval-email-line copyable-email"
                                                                                                                            />
                                                                                                                        )}
                                                                                                                    </div>
                                                                                                                    <div className="approval-supervisor-meta">
                                                                                                                        <div className="approval-supervisor-details">
                                                                                                                            <div className="approval-supervisor-comments">
                                                                                                                                <p className="approval-supervisor-comments-label">
                                                                                                                                    Supervisor comments
                                                                                                                                </p>
                                                                                                                                <p className="approval-supervisor-comments-text">
                                                                                                                                    <ValueWithSkeleton
                                                                                                                                        value={approval.supervisorComments || 'None provided.'}
                                                                                                                                    />
                                                                                                                                </p>
                                                                                                                            </div>
                                                                                                                        </div>
                                                                                                                    </div>
                                                                                                                </div>
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            ) : null}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                            <span className="usa-checkbox__label-description">
                                                                                <label
                                                                                    className="usa-label approval-comments-label"
                                                                                    htmlFor={`entitlement-owner-approval-comments-${approval.id}`}
                                                                                >
                                                                                    Approver Comments (Required for a rejection)
                                                                                </label>
                                                                                <textarea
                                                                                    className="usa-textarea"
                                                                                    id={`entitlement-owner-approval-comments-${approval.id}`}
                                                                                    name={`entitlement-owner-approval-comments-${approval.id}`}
                                                                                    rows="3"
                                                                                    placeholder="Add comments for this approval"
                                                                                    value={
                                                                                        entitlementOwnerApprovalComments[approval.id] ?? ''
                                                                                    }
                                                                                    onChange={(event) =>
                                                                                        updateEntitlementOwnerApprovalComment(
                                                                                            approval.id,
                                                                                            event.target.value
                                                                                        )
                                                                                    }
                                                                                    disabled={isSubmittingEntitlementOwnerAction}
                                                                                />
                                                                                <div className="approval-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="usa-button usa-button--approve"
                                                                                        onClick={(event) => {
                                                                                            event.preventDefault();
                                                                                            event.stopPropagation();
                                                                                            handleApproveSingleEntitlementOwnerApproval(
                                                                                                approval.id
                                                                                            );
                                                                                        }}
                                                                                        disabled={isSubmittingEntitlementOwnerAction}
                                                                                    >
                                                                                        Approve request
                                                                                    </button>
                                                                                    <button
                                                                                        type="button"
                                                                                        className="usa-button usa-button--reject"
                                                                                        onClick={(event) => {
                                                                                            event.preventDefault();
                                                                                            event.stopPropagation();
                                                                                            handleRejectSingleEntitlementOwnerApproval(
                                                                                                approval.id
                                                                                            );
                                                                                        }}
                                                                                        disabled={
                                                                                            isSubmittingEntitlementOwnerAction ||
                                                                                            !String(
                                                                                                entitlementOwnerApprovalComments[
                                                                                                    approval.id
                                                                                                ] ?? ''
                                                                                            ).trim()
                                                                                        }
                                                                                    >
                                                                                        Reject request
                                                                                    </button>
                                                                                </div>
                                                                            </span>
                                                                        </div>
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </fieldset>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </AppLayout>
    );
};

export default ApprovalsPage;
